// CustomerOrderView.js
// The customer-facing order screen. Reached by scanning a Golden Ticket QR code.
// Handles token validation, image upload, framing, and order submission.
// No staff auth required — this is a public-facing view gated only by token ID.
//
// Key behaviors:
// - On successful submit: order ID + queue number stored in localStorage keyed by tokenId
// - On mount: localStorage checked first — if an order was already submitted for this
//   token, skip straight to the confirmed screen (prevents double submission on refresh)
// - Confirmed screen: real-time listener on the order document + active shift document
//   to show live queue position and estimated wait time

import React, { useEffect, useState, useRef } from "react";
import FlashOrderView from "./FlashOrderView";
import PancakeKeepsake from "./components/PancakeKeepsake";
import CampaignLanding from "./components/CampaignLanding";
import BasketManifest from "./components/BasketManifest";
import useColorPalette from "./hooks/useColorPalette";
import useFlashDesigns from "./hooks/useFlashDesigns";
import FramingTool from "./FramingTool";
import { normalizePhotoForUpload } from "./utils/photo";
import { assignDesignSequence } from "./utils/designSequence";
import { BASELINE_FLASH_MIN, BASELINE_CUSTOM_MIN, shiftTempo } from "./utils/overlayFormat";
import { COMBOS, COMBO_ORDER, ADD_ONS, fmtCents, comboUpgradeDeltaCents, shiftSellsGoods } from "./utils/combos";
import { tierPriceCents, comboPriceCents, comboUpgradeDeltaCentsForShift } from "./utils/eventPricing";
import { resolveShift } from "./utils/shiftScope";
import { db, storage } from "./firebase";

import {
  doc, getDoc, addDoc, updateDoc, collection,
  onSnapshot, runTransaction, query,
  where, getDocs, serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

// Resolve the shift a CUSTOMER screen should follow.
//
// `preferredId` is the shiftId stamped on the guest's own token/order. When we
// have it, it wins outright — and when it is NOT among the live shifts, their
// booth has closed, so we report "no shift" rather than quietly substituting
// the other venue's queue. Falling back to resolveShift covers the pre-token
// phases, where a single live shift is still unambiguous.
function scopedActiveShift(snap, preferredId) {
  const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (preferredId) return all.find(s => s.id === preferredId) || null;
  return resolveShift(all).shift;
}

// ── CONSTANTS ────────────────────────────────────────────────────────────────
const BRAND = {
  gold:   "#fcb31f",
  navy:   "#1d52a0",
  blue:   "#1f59db",
  sky:    "#4aa4e1",
  orange: "#f57600",
  pink:   "#ef537e",
  dark:   "#0d1833",
  cream:  "#ffdaad",
};

const sharePillStyle = {
  background: "rgba(255,255,255,0.1)", border: "1px solid rgba(74,164,225,0.6)", color: "white",
  borderRadius: "8px", padding: "0.4rem 0.65rem", fontSize: "0.78rem",
  cursor: "pointer", textDecoration: "none", display: "inline-block",
  whiteSpace: "nowrap", flexShrink: 0,
};

// localStorage key format: "gts_order_{tokenId}"
// Value: JSON { orderId, queueNumber, submittedAt }
function getLocalOrder(tokenId) {
  try {
    const raw = localStorage.getItem(`gts_order_${tokenId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setLocalOrder(tokenId, orderId, queueNumber) {
  try {
    localStorage.setItem(`gts_order_${tokenId}`, JSON.stringify({
      orderId,
      queueNumber,
      submittedAt: Date.now(),
    }));
  } catch { /* storage unavailable — non-fatal */ }
}

// ── IMAGE COMPRESSION ────────────────────────────────────────────────────────
// Resizes to maxSide on the longest dimension and recompresses as JPEG.
// Called client-side before uploading the full image to Firebase Storage.
// TODO: EXIF orientation is not handled. Device photos that embed rotation in EXIF
// (common on iPhones) may appear rotated when drawn to canvas. A future fix would
// read EXIF orientation via a library (e.g. exifr) and apply a counter-rotation
// before drawing. For now, rotated images are accepted as a known limitation.
async function compressImage(file, maxSide, quality) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const longest = Math.max(img.naturalWidth, img.naturalHeight);
      const ratio   = longest > maxSide ? maxSide / longest : 1;
      const w       = Math.round(img.naturalWidth  * ratio);
      const h       = Math.round(img.naturalHeight * ratio);
      const canvas  = document.createElement("canvas");
      canvas.width  = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob(resolve, "image/jpeg", quality);
    };
    img.src = url;
  });
}

// ── QUEUE NUMBER HELPER ───────────────────────────────────────────────────────
// Atomically increments the shift's meta counter and returns the assigned number.
// Extracted so both custom and flash submit paths share the same transaction logic.
async function assignQueueNumber(shiftId) {
  const counterSnap = await getDocs(collection(db, "shifts", shiftId, "meta"));
  if (counterSnap.empty) return null;
  const counterRef = counterSnap.docs[0].ref;
  return runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    if (!counterDoc.exists()) throw new Error("Counter document missing");
    const current = counterDoc.data().lastNumber ?? 0;
    const next    = current + 1;
    transaction.update(counterRef, { lastNumber: next });
    return next;
  });
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
// Small character portrait — decorative, gracefully hidden if image hasn't loaded yet
// ── LIVE GRIDDLE CARD ────────────────────────────────────────────────────────
// "What's happening at the griddle right now" on the waiting screen — reads
// a per-shift summary doc (titles + queue numbers only, written server-side
// on order state changes). Intentionally NOT a video or a bulk order query:
// one tiny doc listen costs the guest bytes, not data.
//
// Per-shift since 2026-09-25 (liveGriddles/{shiftId}). It used to be the one
// config/liveGriddle doc, which two concurrent booths would overwrite in turn
// — the shiftId guard below meant the losing booth's guests simply saw no
// card. Keyed by shift, both booths get their own live view. The old doc is
// still read as a fallback so orders already in flight across the deploy
// don't lose the card mid-shift.
function LiveGriddleCard({ shiftId, myQueueNumber }) {
  const [live, setLive] = useState(null);
  const [legacy, setLegacy] = useState(null);
  useEffect(() => {
    if (!shiftId) { setLive(null); return; }
    return onSnapshot(doc(db, "liveGriddles", shiftId), (snap) => {
      setLive(snap.exists() ? snap.data() : null);
    }, () => setLive(null));
  }, [shiftId]);
  useEffect(() => {
    return onSnapshot(doc(db, "config", "liveGriddle"), (snap) => {
      setLegacy(snap.exists() ? snap.data() : null);
    }, () => setLegacy(null));
  }, []);

  const source = live || legacy;
  if (!source || !shiftId || source.shiftId !== shiftId) return null;
  const live_ = source;
  const fresh = live_.updatedAt?.toMillis ? (Date.now() - live_.updatedAt.toMillis()) < 30 * 60 * 1000 : false;
  const rows = [
    ...(live_.drawing || []).map(x => ({ ...x, icon: "🖌", verb: "Drawing now" })),
    ...(live_.griddle || []).map(x => ({ ...x, icon: "🔥", verb: "On the griddle" })),
    ...(live_.justFinished ? [{ ...live_.justFinished, icon: "✨", verb: "Just finished" }] : []),
  ];
  if (!fresh || rows.length === 0) return null;

  return (
    <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(252,179,31,0.3)", borderRadius: "14px", padding: "0.9rem 1rem", marginTop: "1.2rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.8rem", letterSpacing: "0.04em", marginBottom: "0.55rem" }}>
        LIVE FROM THE GRIDDLE
      </div>
      {rows.map((r, i) => {
        const mine = myQueueNumber != null && r.queueNumber === myQueueNumber;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.55rem", padding: "0.25rem 0" }}>
            <span style={{ fontSize: "1rem" }}>{r.icon}</span>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.74rem", minWidth: "6.2rem" }}>{r.verb}</span>
            <span style={{ color: mine ? BRAND.gold : "rgba(255,255,255,0.85)", fontSize: "0.84rem", fontWeight: mine ? 800 : 600 }}>
              {r.title}{r.queueNumber != null ? ` (#${r.queueNumber})` : ""}{mine ? " — that's you!! 🎉" : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CharacterPortrait({ src, alt, size = 80, style: extra = {} }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      style={{
        width: `${size}px`, height: `${size}px`, objectFit: "contain",
        filter: "drop-shadow(0 0 6px rgba(252,179,31,0.5))",
        ...extra,
      }}
    />
  );
}

export default function CustomerOrderView() {
  const COLOR_OPTIONS = useColorPalette();
  const { byLabel } = useFlashDesigns();
  const params  = new URLSearchParams(window.location.search);
  const tokenId = params.get("t");

  // phases: loading → valid → framing → submitting → confirmed → error → expired → used
  const [phase, setPhase]             = useState("loading");
  const [tokenData, setTokenData]     = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Order form state
  const [selectedImage, setSelectedImage]     = useState(null);
  const [photoConverting, setPhotoConverting] = useState(false);
  const [photoError, setPhotoError]           = useState(null);
  const [croppedBlob, setCroppedBlob]         = useState(null);
  const [recipientName, setRecipientName]     = useState("");
  const [subjectName, setSubjectName]         = useState("");
  const [notes, setNotes]                     = useState("");
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [uploadProgress, setUploadProgress]       = useState(0);

  // Confirmed state — populated on submit or on localStorage restore
  const [orderId, setOrderId]         = useState(null);
  const [queueNumber, setQueueNumber] = useState(null);

  // Push notification subscription state (confirmed screen)


  // Live data from Firestore listeners (confirmed screen)
  const [orderState, setOrderState]   = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [showFullPhoto, setShowFullPhoto] = useState(false);

  // Queue status banner — undefined=loading, null=no active shift, obj=shift found
  const [bannerShift, setBannerShift] = useState(undefined);

  // Which booth does THIS guest belong to? (2026-09-25)
  //
  // Since two events can now run at once, "the active shift" is no longer a
  // meaningful phrase on a customer screen. The guest's ticket knows exactly
  // where it was issued — tokens and orders both carry shiftId — so that is
  // the authority here, not whichever shift Firestore returned first.
  const scopeShiftId = orderState?.shiftId || tokenData?.shiftId || null;
  // Persists FramingTool scale/offset across queue_closed ↔ framing phase transitions
  const framingStateRef               = useRef({ scale: 1, offset: { x: 0, y: 0 }, rotation: 0 });

  // Face detection — MediaPipe BlazeFace
  const faceDetectorRef = useRef(null);
  // rawFaceCount  — from original image, fires once on selection, drives one-time guidance banner
  // cropFaceCount — from live cropped blob, debounced 300ms, drives framing banners + submit guard
  const [rawFaceCount,          setRawFaceCount]          = useState(null);
  const [cropFaceCount,         setCropFaceCount]         = useState(null);
  const [noFaceDismissed,       setNoFaceDismissed]       = useState(false);

  // Landmark-based signals — populated when exactly 1 face is in the circle
  const [faceFillRatio,    setFaceFillRatio]    = useState(null); // number 0-1, face bbox area / circle area
  const [faceOffCenter,    setFaceOffCenter]    = useState(null); // boolean — nose tip far from circle center

  // Complexity + quality — driven by live cropped blob, debounced 400ms
  // Banners auto-hide when metrics improve — no dismiss button needed
  const [complexityLevel, setComplexityLevel] = useState(null);
  const [cropBrightness,  setCropBrightness]  = useState(null); // mean pixel brightness 0–255
  const [cropSharpness,   setCropSharpness]   = useState(null); // Sobel edge density 0–1
  const [isPixelated,     setIsPixelated]     = useState(false);
  const [pixelDismissed,  setPixelDismissed]  = useState(false);
  const [isBlurry,        setIsBlurry]        = useState(false);   // blur — lapVariance < 180
  const [blurDismissed,   setBlurDismissed]   = useState(false);
  const [qualityGrade,    setQualityGrade]    = useState(null); // null | "great"


  // Debounce timer refs for live crop analysis
  const complexityTimerRef = useRef(null);
  const faceTimerRef       = useRef(null);

  // Color preferences — up to 3, stored with order, shown to artist in queue view
  const [selectedColors, setSelectedColors] = useState([]);
  function handleToggleColor(colorId) {
    setSelectedColors(prev =>
      prev.includes(colorId)
        ? prev.filter(id => id !== colorId)
        : prev.length < 3 ? [...prev, colorId] : prev
    );
  }

  // ── SUSPENSION FLOW ───────────────────────────────────────────────────────
  const [copied, setCopied]                         = useState(false);
  const [keepsakeEmail, setKeepsakeEmail]           = useState("");
  const [mailingOptIn, setMailingOptIn]             = useState(false);
  const [emailSending, setEmailSending]             = useState(false);
  const [emailSent, setEmailSent]                   = useState(false);
  const [emailError, setEmailError]                 = useState(null);
  const [resubmitError, setResubmitError]           = useState(null);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [isResubmitting, setIsResubmitting]         = useState(false);
  const isResubmittingRef                           = useRef(false);
  const [resubmitSuccess, setResubmitSuccess]       = useState(false);
  const [showRevertBanner, setShowRevertBanner]     = useState(false);
  const [suspendCountdownMs, setSuspendCountdownMs] = useState(null);
  const prevOrderStateRef                           = useRef(null);
  const shareFileRef                                = useRef(null);

  // ── MOUNT: CHECK LOCALSTORAGE FIRST ───────────────────────────────────────
  // If this token was already submitted on this device, skip straight to confirmed.
  // This handles page refresh without re-rendering the order form.
  useEffect(() => {
    console.time("[GTS] mount→content");

    if (!tokenId) {
      setErrorMessage("No ticket found. Please scan your Golden Ticket QR code.");
      setPhase("error");
      console.timeEnd("[GTS] mount→content");
      return;
    }

    // Check local storage before hitting Firestore
    const localOrder = getLocalOrder(tokenId);
    if (localOrder) {
      setOrderId(localOrder.orderId);
      setQueueNumber(localOrder.queueNumber);
      setPhase("confirmed");
      console.timeEnd("[GTS] mount→content");
      return;
    }

    // No local record — validate token via Firestore
    validateToken().then(() => console.timeEnd("[GTS] mount→content"));
  }, [tokenId]);

  async function validateToken() {
    try {
      console.time("[GTS] validateToken total");
      console.time("[GTS] parallel reads");

      // Kick off both reads in parallel — shift check doesn't need to wait for the token.
      // Both share the same Firestore connection, so firing together eliminates the
      // sequential RTT penalty (was ~half the total load time on slow connections).
      const [tokenSnap, shiftSnap] = await Promise.all([
        getDoc(doc(db, "tokens", tokenId)),
        getDocs(query(collection(db, "shifts"), where("isActive", "==", true))),
      ]);
      console.timeEnd("[GTS] parallel reads");

      if (!tokenSnap.exists()) {
        setErrorMessage("This ticket doesn't exist. Please check your QR code.");
        setPhase("error");
        console.timeEnd("[GTS] validateToken total");
        return;
      }

      const token = tokenSnap.data();

      if (token.status === "submitted" || token.status === "used") {
        // Campaign tickets always render through the flyer landing, regardless of
        // whether a pancake is attached. Attached pancakes appear as the hero
        // image inside the flyer, not as the primary subject — these are event
        // flyers with a pancake hook, not pancake keepsakes for strangers.
        if (token.campaignId) {
          setTokenData(token);
          setPhase("campaign_landing");
          console.timeEnd("[GTS] validateToken total");
          return;
        }
        // Basket tokens — one purchase covering N pancake slots. Routes to the
        // compound-order manifest (live render of baskets/{id}).
        if (token.basketId) {
          setTokenData(token);
          setPhase("basket_manifest");
          console.timeEnd("[GTS] validateToken total");
          return;
        }
        // Legacy promo tokens + normal customer keepsakes — both land here.
        if (token.orderId) {
          setTokenData(token); // preserve token data (needed for promo ticket detection)
          setOrderId(token.orderId);
          setLocalOrder(tokenId, token.orderId, null);
          setPhase("confirmed");
          console.timeEnd("[GTS] validateToken total");
          return;
        }
        setErrorMessage("This ticket has already been used.");
        setPhase("error");
        console.timeEnd("[GTS] validateToken total");
        return;
      }

      if (token.status === "expired") {
        setPhase("expired");
        console.timeEnd("[GTS] validateToken total");
        return;
      }

      if (token.expiresAt && token.expiresAt.toDate() < new Date()) {
        setPhase("expired");
        console.timeEnd("[GTS] validateToken total");
        return;
      }

      setTokenData(token);
      // Custom → tips screen; Magic → magic_choice; Flash → order form
      const dest = token.ticketType === "custom" ? "tips"
                 : token.ticketType === "magic"  ? "magic_choice"
                 : "valid";
      // Scoped (2026-09-25): "is any shift open" would send a guest whose own
      // booth hasn't opened yet straight into the order form because the OTHER
      // venue happened to be live. Their token names their shift; use it.
      // (token.shiftId, not scopeShiftId — state hasn't re-rendered yet.)
      setPhase(scopedActiveShift(shiftSnap, token.shiftId || null) ? dest : "queue_waiting");
      console.timeEnd("[GTS] validateToken total");

    } catch (err) {
      console.error("Token validation error:", err);
      setErrorMessage("Something went wrong loading your ticket. Please try again.");
      setPhase("error");
    }
  }

  // ── LIVE LISTENERS — confirmed screen ─────────────────────────────────────
  // When we reach confirmed, open two real-time listeners:
  //   1. The order document — for state changes (drawing, cooking, done, rejected)
  //   2. The active shift document — for currentNumber + wait time calculation
  useEffect(() => {
    if (phase !== "confirmed" || !orderId) return;

    // Listen to own order document
    const unsubOrder = onSnapshot(doc(db, "orders", orderId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        // Detect auto-revert: Cloud Scheduler explicitly set revertedFromSuspension: true
        if (data.revertedFromSuspension === true && !isResubmittingRef.current) {
          setShowRevertBanner(true);
        }
        prevOrderStateRef.current = data;
        setOrderState(data);
      }
    });

    // Follow THIS guest's shift — the one their order was issued on, not
    // whichever shift a docs[0] read happened to surface.
    const shiftQuery = query(
      collection(db, "shifts"),
      where("isActive", "==", true)
    );
    const unsubShift = onSnapshot(shiftQuery, (snap) => {
      setActiveShift(scopedActiveShift(snap, scopeShiftId));
    });

    return () => {
      unsubOrder();
      unsubShift();
    };
    // scopeShiftId included so the shift listener re-binds once the order doc
    // arrives and tells us which booth this guest is actually standing at.
  }, [phase, orderId, scopeShiftId]);

  // Live shift listener — active across all pre-submission phases.
  // bannerShift: undefined=loading, null=no active shift, obj=shift found.
  // Drives the queue_waiting holding page and auto-advances it when the queue opens.
  const isOrderingPhase = phase === "valid" || phase === "framing" || phase === "queue_closed" || phase === "queue_waiting" || phase === "magic_choice";
  useEffect(() => {
    if (!isOrderingPhase) { setBannerShift(undefined); return; }
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    const unsub = onSnapshot(q, (snap) => {
      setBannerShift(scopedActiveShift(snap, scopeShiftId));
    });
    return () => unsub();
  }, [isOrderingPhase, scopeShiftId]);

  // Auto-advance from holding page when queue opens
  useEffect(() => {
    if (phase === "queue_waiting" && bannerShift) {
      const type = tokenData?.ticketType;
      setPhase(type === "custom" ? "tips" : type === "magic" ? "magic_choice" : "valid");
    }
  }, [phase, bannerShift, tokenData?.ticketType]);

  // Suspension countdown — ticks every second while order is suspended
  useEffect(() => {
    if (orderState?.state !== "suspended") { setSuspendCountdownMs(null); return; }
    const tick = () => {
      const ms = orderState?.suspendedAt?.toMillis?.();
      if (!ms) return;
      setSuspendCountdownMs(Math.max(0, ms + 3 * 60 * 1000 - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [orderState?.state, orderState?.suspendedAt]);

  // Pre-fetch the shareable image as soon as we have a keepsake photo.
  // iOS Safari requires navigator.share() to be called synchronously from a user gesture —
  // awaiting fetch() inside the click handler consumes the activation window and blocks share.
  // Pre-loading the File here means the share button can call navigator.share() with no pre-await.
  useEffect(() => {
    const imgUrl = orderState?.photo_cutout_url || orderState?.photo_url;
    if (!imgUrl) { shareFileRef.current = null; return; }
    fetch(imgUrl)
      .then(r => r.blob())
      .then(blob => {
        const ext = blob.type.includes("png") ? "png" : "jpg";
        shareFileRef.current = new File([blob], `pancake-art.${ext}`, { type: blob.type });
      })
      .catch(() => { shareFileRef.current = null; });
  }, [orderState?.photo_cutout_url, orderState?.photo_url]);

  // Update Open Graph meta tags when a keepsake photo is available.
  // Crawlers that execute JS (iMessage, Slack, Discord, Bluesky) will pick this up.
  // Classic server-side crawlers (WhatsApp) won't — they see the static index.html OG tags.
  useEffect(() => {
    const imgUrl = orderState?.photo_cutout_url || orderState?.photo_url;
    if (!imgUrl) return;
    const description = orderState?.ai_description || "Pancake art made just for you by The Joy of Pancakes.";
    const title = "My Joy of Pancakes Pancake Art";
    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setNameMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("og:image", imgUrl);
    setMeta("og:image:width", "600");
    setMeta("og:image:height", "600");
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("og:url", window.location.href);
    setNameMeta("twitter:image", imgUrl);
    setNameMeta("twitter:title", title);
    setNameMeta("twitter:description", description);
    document.title = title;
  }, [orderState?.photo_cutout_url, orderState?.photo_url, orderState?.ai_description]);

  // ── FACE DETECTION — MediaPipe BlazeFace ─────────────────────────────────
  // Loaded on-demand when the customer enters the framing phase (after picking a photo).
  // Keeping it off the page-load path avoids a 20-30s WASM compile stall on mobile.
  // GPU delegate attempted first; falls back to CPU on init failure (some iOS edge cases).
  useEffect(() => {
    if (phase !== "framing") return;
    if (faceDetectorRef.current) return; // already initialised
    import('@mediapipe/tasks-vision').then(async ({ FaceDetector, FilesetResolver }) => {
      const vision = await FilesetResolver.forVisionTasks('/mediapipe/wasm');
      const opts = {
        baseOptions: { modelAssetPath: '/mediapipe/blaze_face_short_range.tflite', delegate: 'GPU' },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.4,
      };
      try {
        faceDetectorRef.current = await FaceDetector.createFromOptions(vision, opts);
      } catch {
        try {
          opts.baseOptions.delegate = 'CPU';
          faceDetectorRef.current = await FaceDetector.createFromOptions(vision, opts);
        } catch(e) { console.error('MediaPipe init failed', e); }
      }
    }).catch(() => {});
  }, [phase]);

  // Runs once on the original image file — provides early "lots of faces" guidance banner.
  async function runRawFaceCheck(file) {
    try {
      const detector = faceDetectorRef.current;
      if (!detector) return;
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      URL.revokeObjectURL(url);
      // Resize to 640px max — full-res photos have faces that are tiny relative to image
      // dimensions, causing BlazeFace to miss them. Scaling down brings faces into the
      // detection sweet spot.
      const MAX = 640;
      const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.naturalWidth  * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const result = detector.detect(canvas);
      const count = result.detections.length;
      if (count > 30) { setRawFaceCount(null); return; }
      setRawFaceCount(count);
    } catch { setRawFaceCount(null); }
  }

  // Runs on the live cropped blob (debounced 300ms) — drives framing banners + submit guard.
  //
  // The blob is the FULL SQUARE canvas (no circular mask — the SVG circle is visual only).
  // After detection we filter to faces whose CENTER falls inside the inscribed circle
  // (center = 50%/50%, radius = 46% of canvas width).
  //
  // When exactly 1 face is in the circle, landmark keypoints are used to derive:
  //   faceFillRatio   — face bbox area / circle area
  //   faceOffCenter   — nose tip distance from circle center > 35% of radius
  //
  // Keypoint order (BlazeFace short range):
  //   0=right_eye, 1=left_eye, 2=nose_tip, 3=mouth, 4=right_ear, 5=left_ear
  async function runCropFaceCheck(blob) {
    try {
      const detector = faceDetectorRef.current;
      if (!detector) return;
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      URL.revokeObjectURL(url);

      const result = detector.detect(img);
      const all = result.detections;
      if (all.length > 30) { setCropFaceCount(null); return; }

      // Circle-center filter — blob is square, circle inscribed at center with r = 46%
      const W  = img.naturalWidth;
      const cx = W / 2, cy = W / 2, r = W * 0.46;
      const inCircle = all.filter(d => {
        const bb = d.boundingBox;
        const faceCx = bb.originX + bb.width  / 2;
        const faceCy = bb.originY + bb.height / 2;
        const dx = faceCx - cx, dy = faceCy - cy;
        return Math.sqrt(dx * dx + dy * dy) <= r;
      });
      setCropFaceCount(inCircle.length);

      // ── Landmark signals — only when exactly 1 face in circle ─────────────
      if (inCircle.length === 1) {
        const kps = inCircle[0].keypoints || [];
        const noseTip = kps[2] ? { x: kps[2].x * W, y: kps[2].y * W } : null;

        // Fill ratio: face bbox area vs inscribed circle area
        const bb = inCircle[0].boundingBox;
        setFaceFillRatio((bb.width * bb.height) / (Math.PI * r * r));

        // Off-center: nose tip more than 35% of radius from circle center
        if (noseTip) {
          const dx = noseTip.x - cx, dy = noseTip.y - cy;
          setFaceOffCenter(Math.sqrt(dx * dx + dy * dy) > r * 0.35);
        } else {
          setFaceOffCenter(null);
        }
      } else {
        setFaceFillRatio(null);
        setFaceOffCenter(null);
      }
    } catch { setCropFaceCount(null); }
  }

  // ── CROP COMPLEXITY + QUALITY METRICS ─────────────────────────────────────
  // Runs on the live cropped blob (debounced 400ms).
  // Measures the circle contents — not the raw image — so background doesn't pollute the score.
  // Produces: complexityLevel, cropBrightness, cropSharpness (fed into qualityGrade derivation).
  //
  // Complexity signals (center 60%-of-area region):
  //   edgeDensity          — Sobel; high = fine detail / busy linework
  //   colorCount           — 2-bit/channel quantization (64 buckets); high = diverse palette
  //   intraRegionVariance  — pixel variance in non-edge neighborhoods; distinguishes photographic
  //                          texture (fur, fabric, brushstrokes — high variance) from graphic
  //                          content (bold fills, illustration — low variance despite high edges)
  //   backgroundDiscount   — reduced when border is low-saturation/neutral (studio bg, solid walls)
  //   textureBoost         — additive term scaled by intraRegionVariance; only raises score for
  //                          genuinely chaotic photographic content
  //
  //   score = (edgeDensity×w + colorCount/64×w) × backgroundDiscount + textureBoost
  //     high: > 0.65  →  advisory shown only when no face detected
  //     else:          →  silent pass
  //
  // Quality signals (used only via qualityGrade derivation effect):
  //   cropBrightness — mean pixel brightness (0–255)
  //   cropSharpness  — edge density in center; blurry images score near 0
  //
  // Fail-open on any error.
  async function runCropComplexityCheck(blob) {
    try {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      URL.revokeObjectURL(url);

      // 400×400 — higher resolution preserves fine linework and thin edges
      const W = 400, H = 400;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, W, H);
      const { data } = ctx.getImageData(0, 0, W, H);

      // Grayscale + mean brightness
      const gray = new Float32Array(W * H);
      let brightnessSum = 0;
      for (let i = 0; i < W * H; i++) {
        const b = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
        gray[i] = b;
        brightnessSum += b;
      }
      setCropBrightness(brightnessSum / (W * H));

      // ── Center region (60% of area ≈ 310×310) ────────────────────────────
      const cSize = Math.round(Math.sqrt(0.6) * W);
      const cOff  = Math.round((W - cSize) / 2);

      // Pass 1: Sobel edge detection — build edge map + count edges
      const isEdgePx = new Uint8Array(W * H);
      let edgeCount = 0;
      const cInnerArea = (cSize - 2) * (cSize - 2);
      for (let y = cOff + 1; y < cOff + cSize - 1; y++) {
        for (let x = cOff + 1; x < cOff + cSize - 1; x++) {
          const i = y * W + x;
          const gx =
            -gray[i - W - 1] + gray[i - W + 1] +
            -2 * gray[i - 1] + 2 * gray[i + 1] +
            -gray[i + W - 1] + gray[i + W + 1];
          const gy =
            -gray[i - W - 1] - 2 * gray[i - W] - gray[i - W + 1] +
             gray[i + W - 1] + 2 * gray[i + W] + gray[i + W + 1];
          if (Math.sqrt(gx * gx + gy * gy) > 30) { isEdgePx[i] = 1; edgeCount++; }
        }
      }
      const edgeDensity = edgeCount / cInnerArea;
      setCropSharpness(edgeDensity);

      // Pass 2: Intra-region variance — 3×3 neighborhood variance on non-edge pixels.
      // Distinguishes flat graphic fills (low variance despite high edges) from photographic
      // texture like fur, fabric, patterned wallpaper (high variance). The additive
      // textureBoost below only meaningfully raises the score for genuinely chaotic content.
      // Normalization cap 600: flat fills ≈0-30, moderate texture ≈100-300, dense fur/pattern ≈600+
      let intraVarSum = 0, intraVarCount = 0;
      for (let y = cOff + 1; y < cOff + cSize - 1; y++) {
        for (let x = cOff + 1; x < cOff + cSize - 1; x++) {
          if (isEdgePx[y * W + x]) continue;
          let nSum = 0, nSumSq = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const v = gray[(y + dy) * W + (x + dx)];
              nSum += v; nSumSq += v * v;
            }
          }
          const mean = nSum / 9;
          intraVarSum += (nSumSq / 9) - (mean * mean);
          intraVarCount++;
        }
      }
      const intraRegionVariance = intraVarCount > 0 ? intraVarSum / intraVarCount : 0;
      const intraVarNorm = Math.min(intraRegionVariance / 600, 1.0);

      // ── Pixelation check — full-resolution pass (360×360) ───────────────────
      // The 200×200 analysis canvas downscales visible pixel blocks into single pixels,
      // making pixelated images look like normal low-res images. We need to draw at
      // native resolution to keep the blocks intact for tile analysis.
      //
      // Uses "flat tile ratio": fraction of 4×4 tiles with near-zero internal variance.
      // Pixelated images → many flat tiles (inside blocks) + some edges (block boundaries).
      // Blurry images    → many flat tiles BUT almost no edges — this separates them.
      const PW = 360, PH = 360;
      const pCanvas = document.createElement("canvas");
      pCanvas.width = PW; pCanvas.height = PH;
      pCanvas.getContext("2d").drawImage(img, 0, 0, PW, PH);
      const pRaw  = pCanvas.getContext("2d").getImageData(0, 0, PW, PH).data;
      const pGray = new Float32Array(PW * PH);
      for (let i = 0; i < PW * PH; i++)
        pGray[i] = 0.299 * pRaw[i*4] + 0.587 * pRaw[i*4+1] + 0.114 * pRaw[i*4+2];

      const pCSize = Math.round(Math.sqrt(0.6) * PW);
      const pCOff  = Math.round((PW - pCSize) / 2);
      const PT = 4;
      let flatTiles = 0, totalTiles = 0;
      for (let ty = pCOff; ty + PT <= pCOff + pCSize; ty += PT) {
        for (let tx = pCOff; tx + PT <= pCOff + pCSize; tx += PT) {
          let mean = 0;
          for (let dy = 0; dy < PT; dy++)
            for (let dx = 0; dx < PT; dx++)
              mean += pGray[(ty+dy)*PW + (tx+dx)];
          mean /= PT * PT;
          let v = 0;
          for (let dy = 0; dy < PT; dy++)
            for (let dx = 0; dx < PT; dx++) {
              const d = pGray[(ty+dy)*PW + (tx+dx)] - mean;
              v += d * d;
            }
          if (v / (PT * PT) < 15) flatTiles++;
          totalTiles++;
        }
      }
      const flatTileRatio = totalTiles > 0 ? flatTiles / totalTiles : 0;

      // Edge density on the large canvas center — needed to distinguish pixelated from blurry
      let pEdgeCount = 0, pEdgeTotal = 0;
      for (let y = pCOff + 1; y < pCOff + pCSize - 1; y++) {
        for (let x = pCOff + 1; x < pCOff + pCSize - 1; x++) {
          const i = y * PW + x;
          const gx = -pGray[i-PW-1]+pGray[i-PW+1] + -2*pGray[i-1]+2*pGray[i+1] + -pGray[i+PW-1]+pGray[i+PW+1];
          const gy = -pGray[i-PW-1]-2*pGray[i-PW]-pGray[i-PW+1] + pGray[i+PW-1]+2*pGray[i+PW]+pGray[i+PW+1];
          if (Math.sqrt(gx*gx + gy*gy) > 40) pEdgeCount++;
          pEdgeTotal++;
        }
      }
      const pEdgeDensity = pEdgeTotal > 0 ? pEdgeCount / pEdgeTotal : 0;
      // >65% flat tiles + visible edges = pixelated (not just blurry)
      setIsPixelated(flatTileRatio > 0.65 && pEdgeDensity > 0.03);

      // ── Laplacian variance — blur/softness detection ────────────────────────
      // Low variance = image lacks sharp transitions = blurry or heavily compressed.
      // Runs on the same 360×360 canvas center crop (pGray / pCOff / pCSize).
      let lapSumSq = 0, lapN = 0;
      for (let y = pCOff + 1; y < pCOff + pCSize - 1; y++) {
        for (let x = pCOff + 1; x < pCOff + pCSize - 1; x++) {
          const i = y * PW + x;
          const lap = 4*pGray[i] - pGray[i-PW] - pGray[i+PW] - pGray[i-1] - pGray[i+1];
          lapSumSq += lap * lap;
          lapN++;
        }
      }
      const lapVariance = lapN > 0 ? lapSumSq / lapN : 0;
      setIsBlurry(lapVariance < 180);

      const colorBuckets = new Set();
      for (let y = cOff; y < cOff + cSize; y++) {
        for (let x = cOff; x < cOff + cSize; x++) {
          const i = (y * W + x) * 4;
          colorBuckets.add(((data[i] >> 6) << 4) | ((data[i + 1] >> 6) << 2) | (data[i + 2] >> 6));
        }
      }
      const colorCount = colorBuckets.size;

      // ── Border region (outer 15% ≈ 30px) — background characterization ───
      const bThick = Math.round(0.15 * W);
      const borderColors = new Set();
      let borderEdgeCount = 0, borderEdgeTotal = 0;
      let borderSatSum = 0, borderSatCount = 0;

      function sampleBorderPixel(y, x) {
        const pi = (y * W + x) * 4;
        const r = data[pi], g = data[pi + 1], b = data[pi + 2];
        borderColors.add(((r >> 6) << 4) | ((g >> 6) << 2) | (b >> 6));
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        borderSatSum += mx > 0 ? (mx - mn) / mx : 0;
        borderSatCount++;
        if (y > 0 && y < H - 1 && x > 0 && x < W - 1) {
          const gi = y * W + x;
          const gx =
            -gray[gi - W - 1] + gray[gi - W + 1] +
            -2 * gray[gi - 1] + 2 * gray[gi + 1] +
            -gray[gi + W - 1] + gray[gi + W + 1];
          const gy =
            -gray[gi - W - 1] - 2 * gray[gi - W] - gray[gi - W + 1] +
             gray[gi + W - 1] + 2 * gray[gi + W] + gray[gi + W + 1];
          if (Math.sqrt(gx * gx + gy * gy) > 30) borderEdgeCount++;
          borderEdgeTotal++;
        }
      }
      for (let y = 0; y < bThick; y++)
        for (let x = 0; x < W; x++) sampleBorderPixel(y, x);
      for (let y = H - bThick; y < H; y++)
        for (let x = 0; x < W; x++) sampleBorderPixel(y, x);
      for (let y = bThick; y < H - bThick; y++) {
        for (let x = 0; x < bThick; x++) sampleBorderPixel(y, x);
        for (let x = W - bThick; x < W; x++) sampleBorderPixel(y, x);
      }

      const borderColorCount  = borderColors.size;
      const borderEdgeDensity = borderEdgeTotal > 0 ? borderEdgeCount / borderEdgeTotal : 0;
      const meanBorderSat     = borderSatCount > 0 ? borderSatSum / borderSatCount : 0;
      const bgIsUniform       = borderColorCount < 10;
      const bgIsRegular       = borderEdgeDensity > 0.12 && borderColorCount < 18;
      const bgIsNeutralGray   = meanBorderSat < 0.12;
      // Tiered discount: neutral gray backdrop (studio/portrait) → deep discount;
      // uniform/regular background (solid fills, gradients) → moderate discount.
      const backgroundDiscount = bgIsNeutralGray ? 0.45 : (bgIsUniform || bgIsRegular) ? 0.70 : 1.0;

      // Illustration / graphic boost: high edge density + limited color palette signals
      // vector-style content (paper dolls, sticker sheets, linework). Photographic Sobel
      // alone under-scores these because flat fills suppress the colorCount term.
      // Shift weight toward edges when palette is narrow but edges are dense.
      const isIllustration = edgeDensity > 0.08 && colorCount < 32;
      const edgeWeight  = isIllustration ? 0.80 : 0.65;
      const colorWeight = isIllustration ? 0.20 : 0.35;
      // textureBoost rewards photographic texture (intra-region variance) without
      // penalizing flat illustrations — additive so it can't suppress a clean score.
      const textureBoost = 0.15 * intraVarNorm;
      const score = (edgeDensity * edgeWeight + (colorCount / 64) * colorWeight) * backgroundDiscount + textureBoost;

      if (score > 0.65) setComplexityLevel("high");
      else              setComplexityLevel(null);
    } catch { /* fail-open */ }
  }

  // ── IMAGE SELECTION ────────────────────────────────────────────────────────
  // Normalize before framing: iPhone HEIC photos can't be decoded by the
  // browser's <img>, so we convert them to JPEG client-side (heic2any) first.
  // Without this, HEIC files would hit FramingTool's "can't open in browser"
  // dead-end. Also enforces the 10MB Storage cap with a clear message.
  async function handleImageSelected(e) {
    const raw = e.target.files?.[0];
    // Clear the input so re-picking the same file after a failure re-fires onChange.
    if (e.target) e.target.value = "";
    if (!raw) return;
    setPhotoError(null);
    setPhotoConverting(true);
    let file;
    try {
      const res = await normalizePhotoForUpload(raw);
      if (res.error) { setPhotoError(res.error); setPhotoConverting(false); return; }
      file = res.file;
    } catch (err) {
      console.warn("photo normalize threw:", err);
      setPhotoError("Couldn't process that photo. Try a different one (JPEG or PNG works best).");
      setPhotoConverting(false);
      return;
    }
    setPhotoConverting(false);
    setSelectedImage(file);
    // Reset all detection state
    setRawFaceCount(null);
    setCropFaceCount(null);
    setNoFaceDismissed(false);
    setFaceFillRatio(null);
    setFaceOffCenter(null);
    setComplexityLevel(null);
    setCropBrightness(null);
    setCropSharpness(null);
    setIsPixelated(false);
    setPixelDismissed(false);
    setIsBlurry(false);
    setBlurDismissed(false);
    setQualityGrade(null);
    setPhase("framing");
    runRawFaceCheck(file); // one-time early guidance banner
  }

  // Shared feedback shown under the "Add Photo" buttons during/after selection.
  function renderUploadStatus() {
    if (photoConverting) return (
      <p style={{ color: BRAND.cream, fontSize: "0.85rem", textAlign: "center", margin: "0.75rem 0 0", opacity: 0.85 }}>
        ⏳ Getting your photo ready… one sec.
      </p>
    );
    if (photoError) return (
      <p style={{ color: BRAND.pink, fontSize: "0.85rem", textAlign: "center", margin: "0.75rem 0 0", lineHeight: 1.45, fontWeight: 600 }}>
        {photoError}
      </p>
    );
    return null;
  }

  // ── LIVE CROP ANALYSIS — debounced on every crop change ────────────────────
  // Fires whenever the user pans/zooms the framing tool (onCropped fires on every gesture).
  // Complexity fires at 400ms; face detection at 300ms (MediaPipe is ~20-100ms, much faster).
  // Return value cleans up timers if croppedBlob changes before they fire.
  useEffect(() => {
    if (!croppedBlob) return;
    const complexityTimer = setTimeout(() => runCropComplexityCheck(croppedBlob), 400);
    const faceTimer       = setTimeout(() => runCropFaceCheck(croppedBlob), 300);
    return () => { clearTimeout(complexityTimer); clearTimeout(faceTimer); };
  }, [croppedBlob]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── QUALITY GRADE DERIVATION ───────────────────────────────────────────────
  // "Great framing ✓" only fires when exactly one face is detected, all image quality
  // signals are clean, and landmark checks pass (front-facing, not off-center).
  // cropFaceCount === null (check pending) suppresses it so the affirmation never
  // races ahead of face detection completing.
  useEffect(() => {
    if (cropBrightness === null || cropSharpness === null) { setQualityGrade(null); return; }
    if (cropFaceCount === null || cropFaceCount < 1) { setQualityGrade(null); return; }
    if (faceFillRatio === null || faceFillRatio < 0.75) { setQualityGrade(null); return; } // subject must fill ≥75% of circle
    const brightnessOk = cropBrightness >= 40 && cropBrightness <= 235;
    if (!brightnessOk || cropSharpness < 0.03) { setQualityGrade(null); return; }
    if (isPixelated) { setQualityGrade(null); return; }
    if (isBlurry) { setQualityGrade(null); return; }
    if (complexityLevel !== null) { setQualityGrade(null); return; }
    setQualityGrade("great");
  }, [cropFaceCount, faceFillRatio, cropBrightness, cropSharpness, complexityLevel, isPixelated, isBlurry]);

  // ── ORDER SUBMISSION ───────────────────────────────────────────────────────
  // 1. Upload images to Firebase Storage
  // 2. Atomically increment the shift's queue counter to get a queue number
  // 3. Write the order document to Firestore
  // 4. Save orderId + queueNumber to localStorage
  function handleGoBackToResubmit() {
    setIsResubmitting(true);
    isResubmittingRef.current = true;
    setSelectedImage(null);
    setCroppedBlob(null);
    framingStateRef.current = { scale: 1, offset: { x: 0, y: 0 }, rotation: 0 };
    setPhase("valid");
  }

  async function handleResubmit() {
    if (!croppedBlob || !recipientName.trim()) return;
    setResubmitError(null);
    setPhase("submitting");
    try {
      const compressedBlob  = await compressImage(selectedImage, 1920, 0.85);
      setUploadProgress(10);
      const croppedRef      = ref(storage, `orders/${tokenId}/cropped_resub_${Date.now()}.jpg`);
      await uploadBytes(croppedRef, croppedBlob, { contentType: "image/jpeg" });
      const croppedImageUrl = await getDownloadURL(croppedRef);
      setUploadProgress(50);
      const origRef2        = ref(storage, `orders/${tokenId}/original_resub_${Date.now()}.jpg`);
      await uploadBytes(origRef2, compressedBlob, { contentType: "image/jpeg" });
      const imageUrl        = await getDownloadURL(origRef2);
      setUploadProgress(85);
      await updateDoc(doc(db, "orders", orderId), {
        imageUrl,
        croppedImageUrl,
        state:                  "queued",
        suspendedAt:            null,
        resubmittedAt:          serverTimestamp(),
        revertedFromSuspension: false,
        recipientName:          recipientName.trim(),
        subjectName:            subjectName.trim() || null,
        notes:                  notes.trim(),
        favoriteColors:         selectedColors,
        framingDrawnWidthFrac:  framingStateRef.current.drawnWidthFrac  ?? null,
        framingDrawnHeightFrac: framingStateRef.current.drawnHeightFrac ?? null,
        framingOffsetXFrac:     framingStateRef.current.offsetXFrac     ?? null,
        framingOffsetYFrac:     framingStateRef.current.offsetYFrac     ?? null,
      });
      setUploadProgress(100);
      setIsResubmitting(false);
      isResubmittingRef.current = false;
      setResubmitSuccess(true);
      setPhase("confirmed");
    } catch (err) {
      console.error("Resubmit error:", err);
      // Stay in resubmit mode — keep isResubmittingRef.current true so the next
      // submit attempt delegates to handleResubmit instead of running the full
      // token-validation flow (which would reject the already-used token).
      setResubmitError("Couldn't update your order — check your connection and try again.");
      setPhase("framing");
    }
  }

  async function handleSubmit() {
    if (isResubmittingRef.current) { handleResubmit(); return; }
    if (!croppedBlob || !recipientName.trim() || !disclaimerChecked) return;
    if (cropFaceCount >= 5) return; // submit button should already be disabled, belt + suspenders

    // Check for active shift before uploading — block orphaned orders.
    // Scoped to the guest's own booth: if THEIR shift closed, the order is
    // orphaned even while the other venue is still cooking.
    const shiftCheck = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
    if (!scopedActiveShift(shiftCheck, scopeShiftId)) {
      setPhase("queue_closed");
      return;
    }

    setPhase("submitting");

    const TIMEOUT_MS = 30000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("SUBMISSION_TIMEOUT")), TIMEOUT_MS)
    );

    try {
      await Promise.race([timeoutPromise, (async () => {
      // 0a. Re-read token to catch race conditions — bail if no longer issued
      const freshTokenSnap = await getDoc(doc(db, "tokens", tokenId));
      if (!freshTokenSnap.exists() || freshTokenSnap.data().status !== "issued") {
        setErrorMessage("This ticket has already been used or is no longer valid.");
        setPhase("error");
        return;
      }

      // 0b. Compress full image client-side (max 1920px, 85% JPEG)
      const compressedBlob = await compressImage(selectedImage, 1920, 0.85);
      setUploadProgress(10);

      // 1. Upload cropped image
      const imageFileName = `orders/${tokenId}/cropped_${Date.now()}.jpg`;
      const storageRef    = ref(storage, imageFileName);
      await uploadBytes(storageRef, croppedBlob, { contentType: "image/jpeg" });
      const croppedImageUrl = await getDownloadURL(storageRef);
      setUploadProgress(45);

      // 2. Upload compressed full image
      const origFileName = `orders/${tokenId}/original_${Date.now()}.jpg`;
      const origRef      = ref(storage, origFileName);
      await uploadBytes(origRef, compressedBlob, { contentType: "image/jpeg" });
      const imageUrl = await getDownloadURL(origRef);
      setUploadProgress(80);

      // 3. Get queue number from the guest's OWN shift.
      //
      // Scoped 2026-09-25 — this was docs[0], the single highest-stakes coin
      // flip in the app: a guest at one booth could be handed a queue number
      // drawn from the other venue's counter, landing their pancake on a
      // griddle 20 miles away with a number nobody at their booth can call.
      const shiftSnap = await getDocs(
        query(collection(db, "shifts"), where("isActive", "==", true))
      );
      const scopedShift  = scopedActiveShift(shiftSnap, scopeShiftId);
      let assignedNumber = null;
      let shiftId        = null;
      let shiftIsTest    = false;
      if (scopedShift) {
        shiftId        = scopedShift.id;
        // Inherit the test flag from the shift — keeps Lab-driven orders
        // inside the _isTestData seal (purge sweep, public builders, emails).
        shiftIsTest    = scopedShift._isTestData === true;
        assignedNumber = await assignQueueNumber(shiftId);
      }

      // 3b. Untitled custom — draw a number from the "untitled" counter so the
      // piece publishes as "Untitled #45" instead of a bare "Untitled".
      const untitledSeq = subjectName.trim() ? null : await assignDesignSequence("untitled");

      // 4. Write order document
      const orderDoc = {
        tokenId,
        shiftId,
        ...(shiftIsTest ? { _isTestData: true } : {}),
        orderType:             "custom",
        state:                 "queued",
        queueNumber:           assignedNumber,
        ...(untitledSeq != null ? { designSequence: untitledSeq } : {}),
        recipientName:         recipientName.trim(),
        subjectName:           subjectName.trim() || null,
        notes:                 notes.trim(),
        favoriteColors:        selectedColors,
        imageUrl,
        croppedImageUrl,
        submittedAt:           serverTimestamp(),
        originalQueuePosition: assignedNumber,
        suspensionCount:       0,
        portalEnabled:         true,

        // Portal fields
        portal_state:          "active",
        nft_status:            "none",

        // Nullable stubs per spec
        photo_url:             null,
        photo_cutout_url:      null,
        customer_email:        null,
        email_marketing_opt_in: false,
        ipfs_url:              null,
        event_name:            null,
        event_location:        null,
        keepsake_expires_at:   null,
        startedAt:             null,
        flippedAt:             null,
        completedAt:           null,
        rejectedAt:            null,
        suspendedAt:           null,
        resubmittedAt:         null,
        drawingTimeMs:         null,
        cookTimeMs:            null,
        // Framing params — normalized fractions of canvas size, device-independent
        framingDrawnWidthFrac:  framingStateRef.current.drawnWidthFrac  ?? null,
        framingDrawnHeightFrac: framingStateRef.current.drawnHeightFrac ?? null,
        framingOffsetXFrac:     framingStateRef.current.offsetXFrac     ?? null,
        framingOffsetYFrac:     framingStateRef.current.offsetYFrac     ?? null,
        squareOrderRef:         tokenData?.squareOrderId ?? null,
      };

      // Mark token used before writing the order — if this fails, no orphaned order is created
      await updateDoc(doc(db, "tokens", tokenId), { status: "used", usedAt: serverTimestamp() });
      const orderRef = await addDoc(collection(db, "orders"), orderDoc);
      setUploadProgress(95);

      // Backfill orderId onto token — enables portal lookup when customer re-scans QR later
      // Retry up to 3 times; a silent failure here means the QR scan won't work after order creation
      (async () => {
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await updateDoc(doc(db, "tokens", tokenId), { orderId: orderRef.id });
            break;
          } catch (e) {
            if (attempt === 3) console.error("Token backfill failed after 3 attempts:", e);
            else await new Promise(r => setTimeout(r, attempt * 500));
          }
        }
      })();

      // 5. Save to localStorage — prevents double submission on refresh
      setLocalOrder(tokenId, orderRef.id, assignedNumber);

      setOrderId(orderRef.id);
      setQueueNumber(assignedNumber);
      setPhase("confirmed");

      })()]);

    } catch (err) {
      if (err.message === "SUBMISSION_TIMEOUT") {
        setResubmitError("Connection timed out — check your wifi and try again.");
        setPhase("framing");
      } else {
        console.error("Order submission error:", err);
        setErrorMessage("Something went wrong submitting your order. Please try again.");
        setPhase("error");
      }
    }
  }

  // ── ESTIMATED WAIT TIME ────────────────────────────────────────────────────
  // Per spec 11F:
  //   Flash orders ahead × 4 min + Custom orders ahead × 8 min
  //   Orders in DRAWING/COOKING counted at half value
  //   Orders in AWAITING_PHOTO counted at zero
  //   Rounded to nearest 5 min
  // Handle paid queue: verify payment on return (?paid=1)
  const [paymentVerifying, setPaymentVerifying] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false); // double-tap on upgrade once minted twin payment links
  const paymentVerifyAttempted = useRef(false);
  useEffect(() => {
    if (phase !== "confirmed" || !orderId || paymentVerifyAttempted.current) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("paid")) return;

    paymentVerifyAttempted.current = true;
    const tokenParam = params.get("t");
    window.history.replaceState(null, "", `/order?t=${tokenParam}`);
    setPaymentVerifying(true);

    console.log("[GTS] Verifying payment for order:", orderId);
    const verifyFn = httpsCallable(getFunctions(getApp()), "verifyLivestreamPayment");
    verifyFn({ orderId })
      .then((result) => {
        console.log("[GTS] Payment verified:", result.data);
        setPaymentVerifying(false);
      })
      .catch((err) => {
        console.error("[GTS] Payment verify failed:", err);
        alert("Payment verification issue: " + (err.message || "Unknown error. Your payment went through — the artist will promote your order manually."));
        setPaymentVerifying(false);
      });
  }, [phase, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent auto-verify: if the order doc shows a paid-queue checkout that was
  // never verified, retry verification on ANY portal load — not only the
  // ?paid=1 return leg, which proved unreliable (Square's post-payment
  // redirect doesn't always run). No alert on failure: an abandoned checkout
  // legitimately stays on standby, and the webhook + sweep cover the rest.
  useEffect(() => {
    if (phase !== "confirmed" || !orderId || !orderState || paymentVerifyAttempted.current) return;
    if (orderState.queue !== "standby" || !orderState.paidQueuePaymentLinkId || orderState.paidQueueVerified) return;
    paymentVerifyAttempted.current = true;
    const verifyFn = httpsCallable(getFunctions(getApp()), "verifyLivestreamPayment");
    verifyFn({ orderId })
      .then((result) => console.log("[GTS] Silent payment verify:", result.data))
      .catch((err) => console.log("[GTS] Silent payment verify — not paid or failed:", err.message));
  }, [phase, orderId, orderState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combo upgrade return leg (?comboPaid=1) + silent re-verify — the same
  // never-client-only doctrine as the paid-queue rail above: the webhook is
  // primary, this is the courtesy fast path, the 2-min sweep is the backstop.
  const comboVerifyAttempted = useRef(false);
  useEffect(() => {
    if (phase !== "confirmed" || !orderId || comboVerifyAttempted.current) return;
    const params = new URLSearchParams(window.location.search);
    const returning = params.has("comboPaid");
    if (returning) {
      const tokenParam = params.get("t");
      window.history.replaceState(null, "", `/order?t=${tokenParam}`);
    } else {
      // Silent path: only bother when an unverified upgrade link exists.
      if (!orderState) return;
      if (orderState.comboKey || !orderState.comboUpgradePaymentLinkId || orderState.comboUpgradeVerified) return;
    }
    comboVerifyAttempted.current = true;
    const verifyFn = httpsCallable(getFunctions(getApp()), "verifyComboUpgrade");
    verifyFn({ orderId })
      .then((result) => console.log("[GTS] Combo upgrade verify:", result.data))
      .catch((err) => {
        console.log("[GTS] Combo upgrade verify — not paid or failed:", err.message);
        if (returning) alert("We couldn't confirm the combo payment yet — if you completed checkout, it'll land automatically within a couple of minutes.");
      });
  }, [phase, orderId, orderState]); // eslint-disable-line react-hooks/exhaustive-deps

  function computeWaitDisplay() {
    if (!activeShift || !queueNumber) return null;
    const currentNumber = activeShift.currentNumber ?? 0;
    if (currentNumber >= queueNumber) return null; // our pancake is being made

    // Heuristic: gap between currentNumber and our number × avg time per pancake.
    // We intentionally do NOT subtract suspendedCount or awaitingPhotoCount —
    // those are shift-wide totals, not counts of orders specifically between
    // currentNumber and queueNumber, so subtracting them produces false "next!" messages.
    // Weight by the GUEST'S OWN ticket type baseline × live shift tempo
    // (shift.drawTempoSamples) — same model as overlay/artist-view estimates.
    const isFlash = tokenData?.ticketType === "flash";
    const perPancakeMin = (isFlash ? BASELINE_FLASH_MIN : BASELINE_CUSTOM_MIN) * shiftTempo(activeShift);
    const ordersAhead = Math.max(0, queueNumber - currentNumber - 1);
    const rawMinutes  = ordersAhead * perPancakeMin;

    if (rawMinutes <= 0) return "Your pancake is next!";
    const rounded = Math.round(rawMinutes / 5) * 5;
    if (rounded >= 120) return "~2 hr+";
    return `~${rounded} min`;
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  if (phase === "loading")    return <Screen><p style={{ color: "white" }}>Loading your ticket...</p></Screen>;
  if (phase === "error")      return <Screen><JacquesError message={errorMessage} jacquesImg={byLabel?.jacques?.imageUrl} /></Screen>;
  if (phase === "expired")    return <Screen><JacquesExpired jacquesImg={byLabel?.jacques?.imageUrl} /></Screen>;
  if (phase === "campaign_landing") return <CampaignLanding token={{ ...tokenData, id: tokenId }} />;
  if (phase === "basket_manifest") return <BasketManifest token={{ ...tokenData, id: tokenId }} />;
  if (phase === "submitting") return (
    <Screen centered>
      <CharacterPortrait src={byLabel?.poffridge?.imageUrl} alt="Poffridge" size={64} style={{ marginBottom: "0.75rem", opacity: 0.85 }} />
      <p style={{ color: BRAND.gold, fontSize: "1.25rem", marginBottom: "1.5rem", textAlign: "center" }}>Sending your order to the griddle...</p>
      <div style={{ width: "100%", height: "6px", background: "#2565b8", borderRadius: "3px", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${uploadProgress}%`, background: BRAND.gold, borderRadius: "3px", transition: "width 0.4s ease" }} />
      </div>
    </Screen>
  );

  if (phase === "queue_closed") return (
    <Screen>
      <JacquesHeader />
      <div style={{ textAlign: "center", padding: "1rem 0" }}>
        <p style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>
          "Mon dieu — the queue isn't open yet!"
        </p>
        <p style={{ color: "white", lineHeight: 1.6, margin: "1rem 0" }}>
          {tokenData?.ticketType === "flash"
            ? "\"Your order is saved and ready to go. Stay close to the booth, wait for Dan to open the queue, and hit submit the moment he does!\""
            : "\"Do not despair, mon ami. Your photo is beautifully framed and ready to go. Stay close to the booth, wait for Dan to open the queue, and hit submit the moment he does. The perfect pancake is worth the wait!\""
          }
        </p>
        <p style={{ color: BRAND.cream, fontSize: "0.875rem", marginBottom: "1.5rem" }}>
          — Jacques Floppes
        </p>
        <button
          style={{ ...styles.uploadButton, background: BRAND.blue }}
          onClick={() => setPhase(tokenData?.ticketType === "flash" ? "valid" : "framing")}
        >
          ← Back to My Order
        </button>
      </div>
    </Screen>
  );

  // Holding page — token valid but no active shift yet
  if (phase === "queue_waiting") return (
    <Screen centered>
      <style>{`
        @keyframes gts-star-float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
          50%       { transform: translateY(-8px) rotate(15deg); opacity: 1; }
        }
        @keyframes gts-waiting-pulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 1; }
        }
      `}</style>
      <JacquesHeader />
      <div style={{ textAlign: "center" }}>
        <CharacterPortrait src={byLabel?.saturnina?.imageUrl} alt="Saturnina" size={100} style={{ marginBottom: "1rem" }} />
        <div style={{ fontSize: "1.5rem", letterSpacing: "0.5rem", marginBottom: "1.5rem" }}>
          {['★','✦','✧','✭','✯','✰'].map((s, i) => (
            <span key={i} style={{
              display: "inline-block",
              color: BRAND.gold,
              animation: `gts-star-float ${1.8 + i * 0.3}s ease-in-out ${i * 0.2}s infinite`,
            }}>{s}</span>
          ))}
        </div>
        <div style={{
          display: "inline-block",
          background: tokenData?.ticketType === "flash" ? "rgba(252,179,31,0.15)" : "rgba(74,164,225,0.15)",
          border: `1px solid ${tokenData?.ticketType === "flash" ? BRAND.gold : BRAND.sky}`,
          borderRadius: "20px",
          padding: "0.3rem 1rem",
          fontSize: "0.8rem",
          color: tokenData?.ticketType === "flash" ? BRAND.gold : BRAND.sky,
          fontWeight: "bold",
          marginBottom: "1.5rem",
          letterSpacing: "0.05em",
        }}>
          {tokenData?.ticketType === "flash" ? "⚡ Flash Ticket" : "🎨 Custom Ticket"}
        </div>
        <p style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem", marginBottom: "0.5rem" }}>
          "The queue isn't open yet, mon ami!"
        </p>
        <p style={{ color: "white", lineHeight: 1.7, marginBottom: "0.5rem" }}>
          "Your ticket is valid and ready to go. The moment Dan opens the queue,
          you'll be brought right in automatically — no refresh needed."
        </p>
        <p style={{ color: BRAND.cream, fontSize: "0.875rem", marginBottom: "2rem" }}>
          — Jacques Floppes
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
          <span style={{
            display: "inline-block", width: "8px", height: "8px", borderRadius: "50%",
            background: BRAND.gold,
            animation: "gts-waiting-pulse 1.5s ease-in-out infinite",
          }} />
          <span style={{ color: "#a8d0f0", fontSize: "0.85rem" }}>Waiting for Dan to open the queue...</span>
        </div>
      </div>
    </Screen>
  );

  // Tips screen — custom tickets only, IS the landing screen (CTA opens file picker directly)
  if (phase === "tips") return (
    <Screen>
      <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
        <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "96px", marginBottom: "0.5rem" }} />
        <CharacterPortrait src={byLabel?.jacques?.imageUrl} alt="Jacques" size={90} style={{ marginBottom: "0.5rem" }} />
        <h2 style={{ color: BRAND.gold, margin: "0.5rem 0 0", fontSize: "1.2rem" }}>
          Jacques' <s style={{ opacity: 0.5 }}>Rules</s> Guidelines
        </h2>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.25rem" }}>
        <div style={{ background: "rgba(74,225,160,0.12)", border: "1px solid rgba(76,207,130,0.6)", borderRadius: "10px", padding: "0.75rem 1rem" }}>
          <p style={{ color: "#5de0a0", fontWeight: "bold", margin: "0 0 0.4rem", fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>Based</p>
          <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "white", lineHeight: 1.9, fontSize: "0.875rem" }}>
            <li>Close-up portraits — 1 or 2 faces, filling the circle</li>
            <li>Pets with clear markings or expressive eyes</li>
            <li>Bold shapes: logos, icons, simple text</li>
          </ul>
        </div>

        <div style={{ background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.6)", borderRadius: "10px", padding: "0.75rem 1rem" }}>
          <p style={{ color: "#ff7099", fontWeight: "bold", margin: "0 0 0.4rem", fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>Cringe</p>
          <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "white", lineHeight: 1.9, fontSize: "0.875rem" }}>
            <li>Tiny faces from across the room</li>
            <li>Dark, blurry, or low-res photos</li>
            <li>The kafkaesque complexity of banal scenes</li>
          </ul>
        </div>
      </div>

      <label style={styles.uploadButton}>
        📷 I'm ready — add my photo
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageSelected} />
      </label>
      {renderUploadStatus()}
    </Screen>
  );

  // Flash order screen — sticker sheet character picker
  if (phase === "valid" && tokenData?.ticketType === "flash") return (
    <Screen>
      <JacquesHeader />
      <h2 style={{ color: BRAND.gold, marginBottom: "0.25rem", textAlign: "center" }}>⚡ Flash Order ⚡</h2>
      <FlashOrderView
        tokenId={tokenId}
        tokenData={tokenData}
        onConfirmed={(oid, qNum) => {
          setLocalOrder(tokenId, oid, qNum);
          setOrderId(oid);
          setQueueNumber(qNum);
          setPhase("confirmed");
        }}
        onQueueClosed={() => setPhase("queue_closed")}
        onError={(msg) => { setErrorMessage(msg); setPhase("error"); }}
      />
    </Screen>
  );

  // Magic order screen — flash designs grid + "bring your own photo" option
  if (phase === "magic_choice") return (
    <Screen>
      <JacquesHeader />
      <h2 style={{ color: BRAND.sky, marginBottom: "0.1rem", textAlign: "center" }}>✨ Magic Ticket ✨</h2>
      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", margin: "0 0 0.5rem", textAlign: "center" }}>
        Pick a flash design — or bring your own photo
      </p>
      <FlashOrderView
        tokenId={tokenId}
        tokenData={tokenData}
        onConfirmed={(oid, qNum) => {
          setLocalOrder(tokenId, oid, qNum);
          setOrderId(oid);
          setQueueNumber(qNum);
          setPhase("confirmed");
        }}
        onQueueClosed={() => setPhase("queue_closed")}
        onError={(msg) => { setErrorMessage(msg); setPhase("error"); }}
        onSwitchToCustom={() => setPhase("tips")}
      />
    </Screen>
  );

  if (phase === "valid") return (
    <Screen>
      <JacquesHeader />
      <h2 style={{ color: BRAND.gold, marginBottom: "0.75rem", textAlign: "center" }}>🎨 Custom Pancake Order 🎨</h2>
      <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
        <CharacterPortrait src={byLabel?.hermiethewurmy?.imageUrl} alt="Hermiethewurmy" size={100} />
      </div>
      <label style={styles.uploadButton}>
        📷 Add Photo
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageSelected} />
      </label>
      {renderUploadStatus()}
    </Screen>
  );

  // ── FRAMING SCREEN ────────────────────────────────────────────────────────
  if (phase === "framing") return (
    <Screen>
      {/* ── 5+ faces: hard block, non-dismissible ─── */}
      {cropFaceCount >= 5 && (
        <div style={{ background: "rgba(200,0,0,0.25)", border: "1px solid rgba(255,80,80,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#ffaaaa", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>
            Too many faces for a live event! Zoom in to 4 or fewer, or come learn about special orders.
          </p>
        </div>
      )}
      {/* ── 4 faces: strong advisory ─── */}
      {cropFaceCount === 4 && (
        <div style={{ background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.6)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#ffe090", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>
            Four+ faces is a lot to fit — references with 1–2 subjects usually give a better result.
          </p>
        </div>
      )}
      {/* ── 2–3 faces: light advisory ─── */}
      {cropFaceCount >= 2 && cropFaceCount <= 3 && (
        <div style={{ background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.6)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#ffe090", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>
            {cropFaceCount === 3
              ? "Zooming in on one or two faces will get you a more detailed result."
              : "Two faces in frame is great!"}
          </p>
        </div>
      )}
      {/* ── Resubmit error ─── */}
      {resubmitError && (
        <div style={{ background: "rgba(200,0,0,0.25)", border: "1px solid rgba(255,80,80,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#ffaaaa", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>{resubmitError}</p>
        </div>
      )}
      {/* ── Off-center / too small — single adaptive banner, only when 1 face in circle ─── */}
      {cropFaceCount === 1 && (faceOffCenter || (faceFillRatio !== null && faceFillRatio < 0.5)) && (
        <div style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(74,164,225,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#d0eeff", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>
            {faceOffCenter && faceFillRatio !== null && faceFillRatio < 0.5
              ? "Fill the circle with your subject — bigger is better!"
              : faceOffCenter
              ? "Try to center your subject in the circle."
              : "Zoom in to fill the circle — the bigger the better!"}
          </p>
        </div>
      )}
      {/* ── No face in circle, but raw detected one elsewhere — bad framing, valid warning.
            Suppressed when blur is active: detector fails on soft images, don't double-warn. ─ */}
      {cropFaceCount === 0 && rawFaceCount > 0 && !isBlurry && !noFaceDismissed && (
        <div style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(74,164,225,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <p style={{ color: "#d0eeff", fontWeight: "bold", margin: 0, fontSize: "0.875rem", flex: "1 1 auto" }}>
            Center your subject in the circle — we don't see a clear face.
          </p>
          <button onClick={() => setNoFaceDismissed(true)} style={{ background: "rgba(255,255,255,0.18)", color: "white", border: "none", borderRadius: "8px", padding: "0.35rem 0.75rem", fontWeight: "bold", cursor: "pointer", fontSize: "0.8rem", flexShrink: 0 }}>
            Got it
          </button>
        </div>
      )}
      {/* ── Image quality advisory — covers pixelation + both blur tiers.
            Single banner, single dismiss, prevents duplicate "A sharper photo..." copy. ─ */}
      {(isPixelated || isBlurry) && !(pixelDismissed && blurDismissed) && (
        <div style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(74,164,225,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <p style={{ color: "#d0eeff", fontWeight: "bold", margin: 0, fontSize: "0.875rem", flex: "1 1 auto" }}>
            {isPixelated && !isBlurry && !(cropFaceCount === 1 && faceFillRatio !== null && faceFillRatio < 0.12)
              ? "Zoom in to fill the circle — or try a higher-res photo."
              : "A sharper photo will get a better result."}
          </p>
          <button onClick={() => { setPixelDismissed(true); setBlurDismissed(true); }} style={{ background: "rgba(255,255,255,0.18)", color: "white", border: "none", borderRadius: "8px", padding: "0.35rem 0.75rem", fontWeight: "bold", cursor: "pointer", fontSize: "0.8rem", flexShrink: 0 }}>
            Got it
          </button>
        </div>
      )}
      {/* ── Complexity — only when no face detected in circle; auto-suppressed by face presence ─ */}
      {cropFaceCount === 0 && complexityLevel && !isPixelated && (
        <div style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(74,164,225,0.7)", borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
          <p style={{ color: "#d0eeff", fontWeight: "bold", margin: 0, fontSize: "0.875rem" }}>
            Busy frame — a tighter crop usually gives Dan more to work with.
          </p>
        </div>
      )}
<div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem" }}>
        <h2 style={{ color: BRAND.gold, margin: 0 }}>Frame Your Pancake</h2>
        <button
          onClick={() => { setSelectedImage(null); setRawFaceCount(null); setCropFaceCount(null); setNoFaceDismissed(false); setFaceFillRatio(null); setFaceOffCenter(null); setComplexityLevel(null); setCropBrightness(null); setCropSharpness(null); setIsPixelated(false); setPixelDismissed(false); setIsBlurry(false); setBlurDismissed(false); setQualityGrade(null); setPhase(tokenData?.ticketType === "custom" ? "tips" : "valid"); }}
          style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "white", fontSize: "0.8rem", cursor: "pointer", padding: "0.3rem 0.75rem", borderRadius: "8px", fontWeight: "bold" }}
        >
          ← Different photo
        </button>
      </div>
      <FramingTool
        imageFile={selectedImage}
        onCropped={(blob) => setCroppedBlob(blob)}
        initialScale={framingStateRef.current.scale}
        initialOffset={framingStateRef.current.offset}
        initialRotation={framingStateRef.current.rotation}
        onStateChange={(params) => { framingStateRef.current = params; }}
        qualityGrade={qualityGrade}
      />
      <div style={{ margin: "1rem 0 0.25rem" }}>
        <label style={{ ...styles.label, marginBottom: "0.5rem" }}>
          Favorite colors <span style={{ color: "#8ab8e0", fontWeight: "normal" }}>(pick up to 3)</span>
        </label>
        <style>{`
          @keyframes gts-color-pulse {
            0%, 100% { filter: drop-shadow(0 0 4px rgba(252,179,31,0.8)); }
            50%       { filter: drop-shadow(0 0 10px rgba(252,179,31,1)) drop-shadow(0 0 18px rgba(252,179,31,0.5)); }
          }
        `}</style>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "10px", margin: "0.5rem 0 1rem" }}>
          {COLOR_OPTIONS.map(color => {
            const selected = selectedColors.includes(color.id);
            const dimmed   = selectedColors.length >= 3 && !selected;
            return (
              <button
                key={color.id}
                onClick={() => handleToggleColor(color.id)}
                aria-label={color.label}
                style={{
                  width: "100%", aspectRatio: "1 / 1", borderRadius: "50%",
                  background: color.hex,
                  border: selected ? `3px solid ${BRAND.gold}` : "2px solid rgba(255,255,255,0.35)",
                  cursor: dimmed ? "not-allowed" : "pointer",
                  padding: 0, transition: "transform 0.15s ease, opacity 0.2s ease",
                  transform: selected ? "scale(1.12)" : "scale(1)",
                  animation: selected ? "gts-color-pulse 2s ease-in-out infinite" : "none",
                  opacity: dimmed ? 0.4 : 1,
                }}
              />
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: "0.25rem" }}>
        {/* Callout name first — what the artist shouts when the pancake is ready. */}
        <label style={styles.label}>Your first name</label>
        <input
          style={styles.input}
          type="text"
          placeholder="First name"
          value={recipientName}
          onChange={e => setRecipientName(e.target.value)}
          maxLength={40}
        />
        <div style={{ color: "#8ab8e0", fontSize: "0.75rem", marginTop: "0.25rem", lineHeight: 1.4 }}>
          So we can call you over when your pancake is ready.
        </div>
        <div style={{ textAlign: "right", fontSize: "0.75rem", marginTop: "0.1rem", color: (40 - recipientName.length) < 10 ? "#e74c3c" : "#8ab8e0" }}>
          {recipientName.length}/40
        </div>
        {/* Pancake title — what is this a picture of? */}
        <label style={{ ...styles.label, marginTop: "1rem" }}>Title / Subject <span style={{ color: "#8ab8e0", fontWeight: "normal" }}>(optional)</span></label>
        <input
          style={styles.input}
          type="text"
          placeholder="What is this a picture of?"
          value={subjectName}
          onChange={e => setSubjectName(e.target.value)}
          maxLength={60}
        />
        <div style={{ color: "#8ab8e0", fontSize: "0.75rem", marginTop: "0.25rem", lineHeight: 1.4 }}>
          This becomes the piece's name in the gallery. Skip it and we'll number it — like "Untitled #45".
        </div>
        <label style={{ ...styles.label, marginTop: "0.75rem" }}>A note <span style={{ color: "#8ab8e0", fontWeight: "normal" }}>(optional)</span></label>
        <textarea
          style={{ ...styles.input, height: "80px", resize: "vertical" }}
          placeholder="a note, a suggestion, a bit of inspiration…"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          maxLength={120}
        />
        <div style={{ textAlign: "right", fontSize: "0.75rem", marginTop: "0.25rem", color: (120 - notes.length) < 10 ? "#e74c3c" : "#8ab8e0" }}>
          {notes.length}/120
        </div>
        <div style={{ margin: "1rem 0" }}>
          <p style={{ color: "#b8d8f0", fontSize: "0.85rem", lineHeight: 1.5, margin: "0 0 0.5rem" }}>
            We reserve the right to reject any request for being hateful or inappropriate. Your artist may adjust the framing of your image to get the best pancake result.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <input
              type="checkbox"
              id="disclaimer"
              checked={disclaimerChecked}
              onChange={e => setDisclaimerChecked(e.target.checked)}
              style={{ width: "20px", height: "20px", flexShrink: 0 }}
            />
            <label htmlFor="disclaimer" style={{ color: BRAND.cream, fontSize: "0.85rem", cursor: "pointer" }}>
              I understand.
            </label>
          </div>
        </div>
        <button
          style={{
            ...styles.uploadButton,
            background: (croppedBlob && recipientName.trim() && disclaimerChecked && !(cropFaceCount >= 5)) ? BRAND.pink : "rgba(255,255,255,0.12)",
            color:      (croppedBlob && recipientName.trim() && disclaimerChecked && !(cropFaceCount >= 5)) ? "white" : "rgba(255,255,255,0.35)",
            cursor:     (croppedBlob && recipientName.trim() && disclaimerChecked && !(cropFaceCount >= 5)) ? "pointer" : "not-allowed",
            marginTop:  "0.5rem",
          }}
          disabled={!croppedBlob || !recipientName.trim() || !disclaimerChecked || cropFaceCount >= 5}
          onClick={handleSubmit}
        >
          Submit My Order 🥞
        </button>
      </div>

    </Screen>
  );

  if (phase === "confirmed") {
    const currentNumber = activeShift?.currentNumber ?? 0;
    const isStandby     = orderState?.queue === "standby";
    const waitDisplay   = isStandby ? null : computeWaitDisplay();
    const myState          = orderState?.state ?? "queued";
    const isRejected       = myState === "rejected";
    const isDrawing        = myState === "drawing";
    const isCooking        = myState === "cooking";
    const isFlipping       = myState === "flipping";
    const isReadyToDeliver = myState === "ready_to_deliver";
    const isKeepsake       = (myState === "done" || myState === "ready_to_deliver") && !!orderState?.photo_url;
    const isReadyNoPhoto   = (myState === "done" || myState === "ready_to_deliver") && !orderState?.photo_url;

    // ── Combos ──
    const myCombo = orderState?.comboKey ? COMBOS[orderState.comboKey] : null;
    // Goods-only voice: free pancakes with the shift opted into goods
    // commerce (door-paid shows). Party-safe copy — the pancake is free and
    // stays free; the guest buys just the take-home goods.
    const goodsSell = shiftSellsGoods(activeShift);
    // Standby customs buy the full combo through the join checkout; main-queue
    // customs (already paid, or register orders) pay the DELTA through the
    // upgrade rail. Party voice never sees either. Prices are display-only
    // mirrors — the server derives its own from the order doc.
    const canComboUpgrade = !myCombo && orderState?.orderType === "custom" &&
      orderState?.joinVoice !== "free" && orderState?.queue === "main" &&
      myState === "queued";
    const startJoinCheckout = async (comboKey) => {
      if (checkoutBusy) return;
      setCheckoutBusy(true);
      const tid = new URLSearchParams(window.location.search).get("t");
      try {
        const fn = httpsCallable(getFunctions(getApp()), "createLivestreamCheckout");
        const result = await fn({ orderId, tokenId: tid, customerName: orderState?.customerName || "Guest", comboKey: comboKey || undefined });
        window.location.href = result.data.checkoutUrl;
      } catch (err) {
        console.error("[GTS] Checkout error:", err.code, err.message, err);
        alert("Checkout error: " + (err.code || "") + " " + (err.message || "unknown"));
        setCheckoutBusy(false); // only re-arm on failure — success navigates away
      }
    };
    const startComboUpgrade = async (comboKey) => {
      if (checkoutBusy) return;
      setCheckoutBusy(true);
      const tid = new URLSearchParams(window.location.search).get("t");
      try {
        const fn = httpsCallable(getFunctions(getApp()), "createComboUpgradeCheckout");
        const result = await fn({ orderId, tokenId: tid, comboKey });
        window.location.href = result.data.checkoutUrl;
      } catch (err) {
        console.error("[GTS] Combo upgrade checkout error:", err.code, err.message, err);
        alert("Checkout error: " + (err.code || "") + " " + (err.message || "unknown"));
        setCheckoutBusy(false);
      }
    };

    // ── Keepsake view — extracted to standalone component ──────────────
    if (isKeepsake) {
      return <PancakeKeepsake order={{ ...orderState, id: orderId, tokenId, state: myState }} db={db} isPromo={tokenData?.ticketType === "promo"} promoToken={tokenData?.ticketType === "promo" ? tokenData : null} />;
    }
    if (false) { // dead code — original keepsake block preserved for reference during migration
      const hasCutout = !!orderState.photo_cutout_url;
      const imgSrc    = (!showFullPhoto && hasCutout) ? orderState.photo_cutout_url : orderState.photo_url;

      const tags = orderState.ai_tags ?? [];

      return (
        <div style={{ background: BRAND.navy, minHeight: "100vh", fontFamily: "var(--jop-display)", maxWidth: "480px", margin: "0 auto", boxSizing: "border-box" }}>
          {/* Logo */}
          <div style={{ textAlign: "center", padding: "1rem 0 0.5rem" }}>
            <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "72px" }} />
          </div>

          {/* Ready banner — shown when ready_to_deliver; hidden once actually handed off (done) */}
          {isReadyToDeliver && (
            <div style={{ background: "#1a3a1a", borderBottom: "1px solid rgba(76,207,130,0.5)", padding: "0.75rem 1.5rem", textAlign: "center" }}>
              <span style={{ color: "#4caf50", fontWeight: "bold", fontSize: "1rem" }}>🥞 Your pancake is ready — come pick it up!</span>
            </div>
          )}

          {/* Hero image */}
          <div style={{ width: "100%", aspectRatio: "1/1", background: hasCutout && !showFullPhoto ? BRAND.dark : "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={imgSrc} alt="Your pancake" style={{ width: "100%", height: "100%", objectFit: hasCutout && !showFullPhoto ? "contain" : "cover", display: "block", filter: hasCutout && !showFullPhoto ? "drop-shadow(0 8px 24px rgba(0,0,0,0.7)) drop-shadow(0 2px 8px rgba(0,0,0,0.5))" : "none" }} />
          </div>

          {/* Toggle */}
          {hasCutout && (
            <div style={{ textAlign: "center", padding: "0.5rem 1.5rem 0" }}>
              <button onClick={() => setShowFullPhoto(v => !v)} style={{ background: "transparent", border: "1px solid rgba(74,164,225,0.5)", color: "#a8d0f0", borderRadius: "8px", padding: "0.35rem 1rem", fontSize: "0.78rem", cursor: "pointer" }}>
                {showFullPhoto ? "Show Cutout" : "Show Full Photo"}
              </button>
            </div>
          )}

          {/* Diagnostic strip */}
          <div style={{ padding: "1rem 1.5rem 0.5rem", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
              {queueNumber && <span style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.4rem" }}>#{queueNumber}</span>}
              <span style={{ color: "#a8d0f0", fontSize: "0.85rem" }}>
                {[orderState.subjectName, orderState.recipientName || orderState.customerName].filter(Boolean).join(" · for ")}
              </span>
            </div>
          </div>

          {/* Description */}
          {orderState.ai_description && (
            <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
              <p style={{ color: "#c8e4f8", fontSize: "0.88rem", lineHeight: 1.6, margin: 0 }}>{orderState.ai_description}</p>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ padding: "0.75rem 1.5rem", borderBottom: "1px solid rgba(255,255,255,0.12)", display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
              {tags.map(t => <span key={t} style={{ background: "#0d2d7a", border: "1px solid rgba(74,164,225,0.4)", color: "#98c8e8", borderRadius: "20px", padding: "0.2rem 0.6rem", fontSize: "0.75rem" }}>{t}</span>)}
            </div>
          )}

          {/* Share + Email section */}
          {(() => {
            const portalUrl  = window.location.href;
            const shareText  = orderState.ai_description
              ? `${orderState.ai_description}\n\nMade by The Joy of Pancakes 🥞`
              : "Check out my Joy of Pancakes pancake art! 🥞";
            const shareBase  = { title: "My Joy of Pancakes pancake art", text: shareText, url: portalUrl };
            const blueskyHref  = `https://bsky.app/intent/compose?text=${encodeURIComponent(shareText.slice(0, 280) + "\n\n" + portalUrl)}`;
            const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText.slice(0, 400) + "\n\n" + portalUrl)}`;

            function handleShare() {
              if (!navigator.share) return;
              const file = shareFileRef.current;
              if (file && navigator.canShare?.({ files: [file] })) {
                navigator.share({ ...shareBase, files: [file] }).catch(() => {});
              } else {
                navigator.share(shareBase).catch(() => {});
              }
            }

            function handleCopy() {
              navigator.clipboard.writeText(portalUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
            }

            async function handleSendEmail() {
              const trimmed = keepsakeEmail.trim();
              if (!trimmed || emailSending || emailSent) return;
              setEmailSending(true);
              setEmailError(null);
              try {
                const fn = httpsCallable(getFunctions(getApp()), "sendKeepsakeEmail");
                await fn({ orderId, tokenId, email: trimmed, mailingList: mailingOptIn });
                setEmailSent(true);
              } catch (e) {
                setEmailError("Couldn't send — check your email and try again.");
              }
              setEmailSending(false);
            }

            return (
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                {/* Email your keepsake */}
                <div style={{ padding: "1.25rem 1.5rem 1rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.85rem" }}>Send to your email</div>
                  {emailSent ? (
                    <div style={{ background: "rgba(76,207,130,0.12)", border: "1px solid rgba(76,207,130,0.4)", borderRadius: "10px", padding: "0.85rem", textAlign: "center", color: "#4ccf82", fontSize: "0.95rem", fontWeight: "bold" }}>
                      ✓ Sent! Check your inbox.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <input
                          type="email"
                          value={keepsakeEmail}
                          onChange={e => { setKeepsakeEmail(e.target.value); setEmailError(null); }}
                          placeholder="your@email.com"
                          style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.4)", borderRadius: "8px", color: "white", fontSize: "0.95rem", padding: "0.65rem 0.85rem", outline: "none" }}
                        />
                        <button
                          onClick={handleSendEmail}
                          disabled={!keepsakeEmail.trim() || emailSending}
                          style={{ background: keepsakeEmail.trim() && !emailSending ? BRAND.gold : "rgba(255,255,255,0.1)", color: keepsakeEmail.trim() && !emailSending ? "#13449b" : "rgba(255,255,255,0.3)", border: "none", borderRadius: "8px", padding: "0.65rem 1rem", fontSize: "0.88rem", fontWeight: "bold", cursor: keepsakeEmail.trim() && !emailSending ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
                        >
                          {emailSending ? "Sending…" : "Send 🥞"}
                        </button>
                      </div>
                      {emailError && <div style={{ color: "#f87171", fontSize: "0.78rem", marginTop: "0.4rem" }}>{emailError}</div>}
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.65rem", cursor: "pointer" }}>
                        <input type="checkbox" checked={mailingOptIn} onChange={e => setMailingOptIn(e.target.checked)} style={{ accentColor: BRAND.gold, width: "16px", height: "16px", flexShrink: 0 }} />
                        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", lineHeight: 1.4 }}>Keep me posted on Joy of Pancakes events</span>
                      </label>
                    </>
                  )}
                </div>

                {/* Share pills */}
                <div style={{ padding: "0 1.5rem 1.25rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.65rem" }}>Share your pancake art</div>
                  {typeof navigator.share === "function" && (
                    <button onClick={handleShare} style={{ width: "100%", background: BRAND.pink, color: "white", border: "none", borderRadius: "10px", padding: "0.85rem", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", marginBottom: "0.65rem" }}>
                      Share
                    </button>
                  )}
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "nowrap", overflowX: "auto" }}>
                    <button onClick={handleCopy} style={sharePillStyle}>{copied ? "Copied!" : "Copy Link"}</button>
                    <a href={blueskyHref}  target="_blank" rel="noopener noreferrer" style={sharePillStyle}>Bluesky</a>
                    <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={sharePillStyle}>WhatsApp</a>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Tip your artist */}
          <div style={{ padding: "1.25rem 1.5rem", background: "rgba(255,255,255,0.04)" }}>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.88rem", margin: "0 0 0.7rem", lineHeight: 1.5 }}>
              🫙 Loved your pancake? Tips go straight to your artist.
            </p>
            <div style={{ display: "flex", gap: "0.6rem" }}>
              <a href="https://venmo.com/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, display: "block", textAlign: "center", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", padding: "0.7rem", color: "rgba(255,255,255,0.7)", fontSize: "0.88rem", fontWeight: "600", textDecoration: "none" }}>
                Venmo
              </a>
              <a href="https://cash.app/$thejoyofpancakes" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, display: "block", textAlign: "center", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", padding: "0.7rem", color: "rgba(255,255,255,0.7)", fontSize: "0.88rem", fontWeight: "600", textDecoration: "none" }}>
                Cash App
              </a>
            </div>
          </div>

          {/* Preservation options */}
          {(() => {
            const isLivestream = orderState?.source === "livestream";
            const preservationRequested = !!orderState?.preservationOption;

            async function startCheckout(option, addFrame = false) {
              try {
                const fn = httpsCallable(getFunctions(getApp()), "createPreservationCheckout");
                const result = await fn({ orderId, option, addFrame });
                // Use location.href not window.open — Safari blocks popups in async callbacks
                window.location.href = result.data.checkoutUrl;
              } catch (err) {
                console.error("[GTS] Preservation checkout error:", err);
                alert("Checkout error: " + (err.code || "") + " " + (err.message || "unknown"));
              }
            }

            return (
              <div style={{ padding: "1.25rem 1.5rem", background: "rgba(252,179,31,0.06)", borderTop: "1px solid rgba(252,179,31,0.15)" }}>
                <p style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", margin: "0 0 0.25rem" }}>
                  Preserve your pancake forever
                </p>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", margin: "0 0 0.75rem", lineHeight: 1.5 }}>
                  {isLivestream
                    ? "We'll preserve your pancake and ship it to you, ready to display."
                    : "Take home a preservation kit, or let us handle everything."}
                </p>
                {preservationRequested ? (
                  <div style={{ background: "rgba(112,190,68,0.15)", border: "1px solid rgba(112,190,68,0.3)", borderRadius: "10px", padding: "0.7rem", textAlign: "center", color: "#70be44", fontSize: "0.88rem", fontWeight: "bold" }}>
                    ✓ Preservation requested — check your email for details
                  </div>
                ) : isLivestream ? (
                  <button onClick={() => startCheckout("livestream_ship")} style={{ width: "100%", background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", padding: "0.85rem", fontWeight: "bold", fontSize: "0.95rem", cursor: "pointer" }}>
                    Preserve & Ship — $65
                  </button>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button onClick={() => startCheckout("kit", false)} style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.7rem", color: "white", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}>
                        DIY Kit — $15
                      </button>
                      <button onClick={() => startCheckout("kit", true)} style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.7rem", color: "white", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}>
                        Kit + Frame — $20
                      </button>
                    </div>
                    <button onClick={() => startCheckout("full_service")} style={{ width: "100%", background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", padding: "0.85rem", fontWeight: "bold", fontSize: "0.95rem", cursor: "pointer" }}>
                      Full Service — $125 + shipping
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Booking upsell */}
          <div style={{ padding: "1.5rem", background: BRAND.dark }}>
            <p style={{ color: "white", fontWeight: "bold", fontSize: "1.15rem", margin: "0 0 0.35rem" }}>Yeah, we do parties.</p>
            <p style={{ color: "#98c8e8", fontSize: "0.85rem", lineHeight: 1.55, margin: "0 0 1.1rem" }}>
              Bring Dr. Dan's World Famous Pancake Art and Wellamade's Gourmet Hot Poffertjes to your next special event.
            </p>
            <a href="https://thejoyofpancakes.com" target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: BRAND.gold, color: "#1d52a0", borderRadius: "10px", padding: "0.85rem", fontWeight: "bold", fontSize: "0.95rem", textDecoration: "none" }}>
              Book an Event
            </a>
          </div>
        </div>
      );
    }

    const isSuspended = myState === "suspended";
    const fmtCountdown = (ms) => {
      if (ms == null || ms < 0) return "0:00";
      const s = Math.ceil(ms / 1000);
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    };

    // ── Standard waiting screen ──────────────────────────────────────────
    return (
      <Screen>
        {/* Suspension confirmation modal */}
        {showSuspendConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1.5rem" }}>
            <div style={{ background: "#0d2d7a", borderRadius: "16px", padding: "1.75rem 1.5rem", maxWidth: "320px", width: "100%", textAlign: "center", border: "1px solid rgba(74,164,225,0.4)" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🥞</div>
              <p style={{ color: BRAND.cream, fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
                "Heads up, mon ami — switching images will hold your spot while you re-submit. If you don't re-submit within 3 minutes, your original request goes back in automatically. Either way, your place in line is safe."
              </p>
              <p style={{ color: BRAND.gold, fontSize: "0.85rem", margin: "0 0 1.5rem" }}>— Jacques</p>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button onClick={() => setShowSuspendConfirm(false)}
                  style={{ flex: 1, padding: "0.9rem", background: "#0e3182", color: "#b8d8f0", border: "none", borderRadius: "10px", fontSize: "0.95rem", fontWeight: "bold", cursor: "pointer" }}>
                  Keep my order
                </button>
                <button onClick={async () => {
                  setShowSuspendConfirm(false);
                  try {
                    await updateDoc(doc(db, "orders", orderId), { state: "suspended", suspendedAt: serverTimestamp(), suspendedBy: "customer" });
                    handleGoBackToResubmit();
                  } catch (e) {
                    console.error("Suspend failed:", e);
                    alert("Couldn't switch images right now — please try again.");
                  }
                }}
                  style={{ flex: 1, padding: "0.9rem", background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", fontSize: "0.95rem", fontWeight: "bold", cursor: "pointer" }}>
                  Yes, switch it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resubmit success banner */}
        {resubmitSuccess && (
          <div style={{ background: "#1a3a1a", border: "1px solid rgba(76,207,130,0.5)", borderRadius: "10px", padding: "0.75rem 1rem", marginBottom: "0.75rem", textAlign: "center" }}>
            <p style={{ color: "#4caf50", fontSize: "0.9rem", margin: 0, lineHeight: 1.5 }}>
              "Magnifique — your request has been updated! You're back in the queue."
            </p>
            <p style={{ color: BRAND.gold, fontSize: "0.78rem", margin: "0.25rem 0 0" }}>— Jacques</p>
          </div>
        )}

        {/* Auto-revert banner */}
        {showRevertBanner && (
          <div style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(74,164,225,0.5)", borderRadius: "10px", padding: "0.75rem 1rem", marginBottom: "0.75rem", textAlign: "center" }}>
            <p style={{ color: BRAND.cream, fontSize: "0.9rem", margin: 0, lineHeight: 1.5 }}>
              "Your original request has been restored and you're back in the queue, mon ami. No harm done!"
            </p>
            <p style={{ color: BRAND.gold, fontSize: "0.78rem", margin: "0.25rem 0 0" }}>— Jacques</p>
          </div>
        )}

        {/* Logo + queue number */}
        <div style={{ textAlign: "center", padding: "1rem 0 0.5rem" }}>
          <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "88px", marginBottom: "0.75rem" }} />
          {(queueNumber || orderState?.queueNumber) && (
            <>
              <div style={{ fontSize: "5rem", fontWeight: "bold", color: isSuspended ? "rgba(255,255,255,0.25)" : BRAND.gold, lineHeight: 1 }}>
                #{queueNumber || orderState?.queueNumber}
              </div>
              <p style={{ color: BRAND.cream, fontSize: "1.1rem", margin: "0.5rem 0" }}>
                You're in the queue!
              </p>
              {byLabel?.sunnynsugar?.imageUrl && (
                <div style={{ marginTop: "0.5rem" }}>
                  <CharacterPortrait src={byLabel.sunnynsugar.imageUrl} alt="Sunny & Sugar" size={100} />
                  <p style={{ color: BRAND.cream, fontSize: "0.85rem", fontStyle: "italic", margin: "0.35rem 0 0", opacity: 0.8 }}>
                    "We're so excited!"
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ background: "rgba(13,24,51,0.6)", borderRadius: "12px", padding: "1rem", margin: "0.75rem 0", textAlign: "center", border: "1px solid rgba(74,164,225,0.2)" }}>
          {isRejected && (
            <p style={{ color: BRAND.orange, fontWeight: "bold" }}>
              "Mon dieu... your order couldn't be fulfilled. Please see a staff member — they'll take care of you with a full refund."
            </p>
          )}
          {isReadyNoPhoto && (
            <p style={{ color: isReadyToDeliver ? "#4caf50" : "#a8d0f0", fontWeight: "bold" }}>
              {isReadyToDeliver
                ? "🥞 Your pancake is ready! Come pick it up!"
                : "🥞 Thanks for visiting! Hope you loved your pancake art."}
            </p>
          )}
          {isReadyToDeliver && (orderState?.joinVoice !== "free" || goodsSell) && (
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", margin: "0.4rem 0 0" }}>
              🖼️ Want it on paper? Ask at the booth for a $10 souvenir print of your pancake.
            </p>
          )}
          {isDrawing && (
            <p style={{ color: BRAND.sky, fontWeight: "bold" }}>
              🎨 Dan is drawing your pancake right now — head back to the griddle!
            </p>
          )}
          {isCooking && (
            <p style={{ color: "#f5a623", fontWeight: "bold" }}>
              🍳 Your pancake is on the griddle — almost time for the flip!
            </p>
          )}
          {isFlipping && (
            <p style={{ color: BRAND.orange, fontWeight: "bold" }}>
              🔥 HERE COMES THE FLIP! Get over here and get your phone out!
            </p>
          )}
          {isSuspended && (orderState?.suspendedBy === "staff" ? (
            // Staff hold (guest wasn't at the booth when called) — indefinite,
            // no countdown, no resubmit. Staff resume it in person; showing the
            // customer 3-minute "switch images" timer here would count down to
            // 0:00 and sit there looking broken.
            <div>
              <p style={{ color: BRAND.cream, fontSize: "0.9rem", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
                "We called your name but couldn't find you! Your pancake is safe on hold — come say hi at the booth and we'll pop you right back in line."
              </p>
              <p style={{ color: BRAND.gold, fontSize: "0.78rem", margin: 0 }}>— Jacques</p>
            </div>
          ) : (
            <div>
              <p style={{ color: BRAND.cream, fontSize: "0.9rem", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
                "Vite, vite! Re-submit before the timer runs out — your original request is standing by."
              </p>
              <p style={{ color: BRAND.gold, fontSize: "0.78rem", margin: "0 0 1rem" }}>— Jacques</p>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: suspendCountdownMs != null && suspendCountdownMs < 30000 ? BRAND.orange : BRAND.cream, marginBottom: "1rem", fontVariantNumeric: "tabular-nums" }}>
                {fmtCountdown(suspendCountdownMs)}
              </div>
              <button onClick={handleGoBackToResubmit}
                style={{ background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", padding: "0.8rem 1.5rem", fontWeight: "bold", fontSize: "0.95rem", cursor: "pointer" }}>
                ← Go back to my image
              </button>
            </div>
          ))}
          {myState === "queued" && (
            <>
              {currentNumber > 0 && (
                <p style={{ color: "#b8d8f0", fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                  Dan is working on <span style={{ color: BRAND.gold, fontWeight: "bold" }}>#{currentNumber}</span>
                </p>
              )}
              {isStandby && myState === "queued" && (
                <>
                  <p style={{ color: BRAND.sky, fontSize: "1rem", fontWeight: "bold" }}>
                    {paymentVerifying ? "Verifying payment..."
                      : orderState?.joinVoice === "free"
                      ? "You're in line — watch the screen for your name!"
                      : "You're on standby — we'll get to you when there's an opening!"}
                  </p>
                  {/* Upgrade CTA — never for party-guest orders (joinVoice
                      "free", stamped by livestreamJoin): a client-paid
                      event's guest must never see commerce language. */}
                  {!paymentVerifying && orderState?.queue === "standby" && orderState?.joinVoice !== "free" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
                      <button
                        disabled={checkoutBusy}
                        onClick={() => startJoinCheckout(null)}
                        style={{ background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", padding: "0.75rem 1.5rem", fontWeight: "bold", fontSize: "0.9rem", cursor: "pointer", opacity: checkoutBusy ? 0.6 : 1 }}
                      >
                        {/* Price mirrors the server-side tier (Road Mode shift pricing included) */}
                        {checkoutBusy ? "Opening checkout..." : <>Get a Guaranteed Spot — {fmtCents(tierPriceCents(activeShift, orderState?.orderType))}</>}
                      </button>
                      {/* Custom standbys can buy straight into a combo — same
                          join checkout, comboKey priced server-side. */}
                      {orderState?.orderType === "custom" && !myCombo && (
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
                          {COMBO_ORDER.map((key) => (
                            <button key={key} disabled={checkoutBusy} onClick={() => startJoinCheckout(key)}
                              style={{ background: "rgba(252,179,31,0.1)", border: "1px solid rgba(252,179,31,0.45)", borderRadius: "8px", padding: "0.5rem 0.9rem", color: BRAND.gold, fontSize: "0.8rem", fontWeight: "bold", cursor: "pointer", opacity: checkoutBusy ? 0.6 : 1 }}>
                              {COMBOS[key].emoji} {COMBOS[key].label} — {fmtCents(comboPriceCents(activeShift, key))}
                            </button>
                          ))}
                        </div>
                      )}
                      {orderState?.paidQueuePaymentLinkId && (
                        <button
                          onClick={async () => {
                            setPaymentVerifying(true);
                            try {
                              const verifyFn = httpsCallable(getFunctions(getApp()), "verifyLivestreamPayment");
                              const result = await verifyFn({ orderId });
                              console.log("[GTS] Manual verify result:", result.data);
                              alert(result.data.promoted ? `Promoted to #${result.data.queueNumber}!` : "Already in queue.");
                            } catch (err) {
                              alert("Verification failed: " + (err.message || "Unknown error"));
                            }
                            setPaymentVerifying(false);
                          }}
                          style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "0.5rem 1rem", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", cursor: "pointer" }}
                        >
                          Already paid? Tap to verify
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
              {!isStandby && waitDisplay && (
                <p style={{ color: BRAND.cream, fontSize: "1rem", fontWeight: "bold" }}>
                  Estimated wait: {waitDisplay}
                </p>
              )}
              {!isStandby && !waitDisplay && !currentNumber && (
                <p style={{ color: "#8ab8e0", fontSize: "0.85rem" }}>
                  Waiting for queue to start...
                </p>
              )}
              {/* Combo upgrade — a paid/registered custom already in the main
                  queue pays only the DELTA. Never shown in party voice. */}
              {canComboUpgrade && (
                <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "rgba(252,179,31,0.07)", border: "1px solid rgba(252,179,31,0.3)", borderRadius: "10px" }}>
                  <p style={{ color: BRAND.gold, fontSize: "0.9rem", fontWeight: "bold", margin: "0 0 0.5rem" }}>
                    🎁 Make it a combo — take your pancake home forever
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
                    {COMBO_ORDER.map((key) => (
                      <button key={key} disabled={checkoutBusy} onClick={() => startComboUpgrade(key)}
                        style={{ background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "8px", padding: "0.55rem 0.9rem", fontSize: "0.82rem", fontWeight: "bold", cursor: "pointer", opacity: checkoutBusy ? 0.6 : 1 }}>
                        {COMBOS[key].emoji} {COMBOS[key].label} +{fmtCents(comboUpgradeDeltaCentsForShift(activeShift, key))}
                      </button>
                    ))}
                  </div>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", margin: "0.5rem 0 0", lineHeight: 1.4 }}>
                    Artist = framed preservation kit · Collector = framed kit + a signed print with your pancake's GTS number
                  </p>
                  {orderState?.comboUpgradePaymentLinkId && !orderState?.comboUpgradeVerified && (
                    <button
                      onClick={async () => {
                        try {
                          const verifyFn = httpsCallable(getFunctions(getApp()), "verifyComboUpgrade");
                          const result = await verifyFn({ orderId });
                          alert(result.data.applied || result.data.already ? "Combo confirmed! 🎉" : "Not confirmed yet.");
                        } catch (err) {
                          alert("Not confirmed yet: " + (err.message || "try again in a moment"));
                        }
                      }}
                      style={{ marginTop: "0.5rem", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "0.4rem 0.9rem", color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", cursor: "pointer" }}>
                      Already paid? Tap to verify
                    </button>
                  )}
                </div>
              )}
            </>
          )}
          {/* Combo confirmation — rides every active state so the guest (and
              whoever they show the phone to at pickup) can see what's included. */}
          {myCombo && (
            <p style={{ color: BRAND.gold, fontSize: "0.85rem", fontWeight: "bold", marginTop: "0.6rem" }}>
              {myCombo.emoji} {goodsSell ? myCombo.goodsLabel : myCombo.label} — includes {(orderState?.addOns || []).map((a) => ADD_ONS[a.type] ? `${ADD_ONS[a.type].emoji} ${ADD_ONS[a.type].label}` : a.type).join(" + ")}
              <br />
              <span style={{ color: "rgba(255,255,255,0.55)", fontWeight: "normal", fontSize: "0.75rem" }}>Collect at the booth when you pick up your pancake</span>
            </p>
          )}
        </div>

        {/* Goods offer — free-pancake shifts with goods commerce on. The
            pancake stays free; these buy just the take-home bundle. Shown
            through the whole make (drawing/cooking is peak want-to-keep-it). */}
        {goodsSell && !myCombo && ["queued", "drawing", "cooking", "flipping"].includes(myState) && (
          <div style={{ background: "rgba(252,179,31,0.08)", border: "1px solid rgba(252,179,31,0.35)", borderRadius: "12px", padding: "1rem", margin: "0.75rem 0", textAlign: "center" }}>
            <p style={{ color: BRAND.gold, fontSize: "0.95rem", fontWeight: "bold", margin: "0 0 0.25rem" }}>
              🎁 Take tonight's pancake home forever
            </p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", margin: "0 0 0.6rem", lineHeight: 1.4 }}>
              Your pancake is free — add a take-home kit and it lasts a lifetime.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "center" }}>
              {COMBO_ORDER.map((key) => (
                <button key={key} disabled={checkoutBusy} onClick={() => startComboUpgrade(key)}
                  style={{ width: "100%", maxWidth: "300px", background: BRAND.gold, color: "#1d52a0", border: "none", borderRadius: "10px", padding: "0.7rem 1rem", fontWeight: "bold", fontSize: "0.88rem", cursor: "pointer", opacity: checkoutBusy ? 0.6 : 1 }}>
                  {COMBOS[key].emoji} {COMBOS[key].goodsLabel} — {fmtCents(comboUpgradeDeltaCents(key))}
                </button>
              ))}
            </div>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", margin: "0.55rem 0 0", lineHeight: 1.4 }}>
              Kit = everything to preserve it at home (display frame +$5 at the booth) · Collector's = framed kit + a signed print with your pancake's GTS number
            </p>
            {orderState?.comboUpgradePaymentLinkId && !orderState?.comboUpgradeVerified && (
              <button
                onClick={async () => {
                  try {
                    const verifyFn = httpsCallable(getFunctions(getApp()), "verifyComboUpgrade");
                    const result = await verifyFn({ orderId });
                    alert(result.data.applied || result.data.already ? "You're all set! 🎉" : "Not confirmed yet.");
                  } catch (err) {
                    alert("Not confirmed yet: " + (err.message || "try again in a moment"));
                  }
                }}
                style={{ marginTop: "0.5rem", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "0.4rem 0.9rem", color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", cursor: "pointer" }}>
                Already paid? Tap to verify
              </button>
            )}
          </div>
        )}

        <p style={{ color: "#8ab8e0", lineHeight: 1.6, fontSize: "0.85rem", textAlign: "center" }}>
          "Keep this page open and I'll update you when Dan starts your pancake
          and the moment the FLIP arrives. Stay close to the griddle!"
          <br /><span style={{ color: BRAND.gold }}>— Jacques</span>
        </p>

        {/* Live griddle — what's being made right now (one tiny PII-safe doc,
            maintained server-side by updateLiveGriddle; bytes per update). */}
        <LiveGriddleCard shiftId={orderState?.shiftId} myQueueNumber={queueNumber} />

        {/* "Switch images?" — subtle, custom orders only, queued state only */}
        {myState === "queued" && orderState?.orderType === "custom" && (
          <p style={{ textAlign: "center", marginTop: "1.5rem" }}>
            <button onClick={() => setShowSuspendConfirm(true)}
              style={{ background: "none", border: "none", color: "#4aa4e1", fontSize: "0.78rem", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              Switch images?
            </button>
          </p>
        )}
      </Screen>
    );
  }

  return null;
}

// ── FRAMING TOOL — see src/FramingTool.js (imported at top) ──────────────────

// ── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function Screen({ children, centered }) {
  return (
    <div style={{
      background:     BRAND.navy,
      minHeight:      "100vh",
      padding:        "1.5rem",
      fontFamily:     "var(--jop-display)",
      maxWidth:       "480px",
      margin:         "0 auto",
      boxSizing:      "border-box",
      ...(centered && { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }),
    }}>
      {children}
    </div>
  );
}

function JacquesHeader() {
  return (
    <div style={{ textAlign: "center", marginBottom: "1rem" }}>
      <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "96px", marginBottom: "0.25rem" }} />
    </div>
  );
}

function JacquesError({ message, jacquesImg }) {
  return (
    <div style={{ textAlign: "center", padding: "2rem 0" }}>
      {jacquesImg
        ? <CharacterPortrait src={jacquesImg} alt="Jacques" size={100} style={{ marginBottom: "0.75rem" }} />
        : <div style={{ fontSize: "3rem" }}>🥞</div>
      }
      <p style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>Sacré bleu!</p>
      <p style={{ color: "white", lineHeight: 1.6 }}>{message}</p>
      <p style={{ color: BRAND.cream, fontSize: "0.875rem" }}>Please see a staff member for help.</p>
    </div>
  );
}

function JacquesExpired({ jacquesImg }) {
  return (
    <div style={{ textAlign: "center", padding: "2rem 0" }}>
      {jacquesImg
        ? <CharacterPortrait src={jacquesImg} alt="Jacques" size={100} style={{ marginBottom: "0.75rem" }} />
        : <div style={{ fontSize: "3rem" }}>🥞</div>
      }
      <p style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>Oh non... this ticket has expired!</p>
      <p style={{ color: "white", lineHeight: 1.6 }}>
        This Golden Ticket is past its scan window. Please see a staff member — they'll take care of you.
      </p>
    </div>
  );
}

// ── STYLES ───────────────────────────────────────────────────────────────────
const styles = {
  uploadButton: {
    display:       "block",
    width:         "100%",
    padding:       "1rem",
    background:    BRAND.pink,
    color:         "white",
    fontWeight:    "bold",
    fontSize:      "1.1rem",
    textAlign:     "center",
    borderRadius:  "12px",
    cursor:        "pointer",
    border:        "none",
    boxSizing:     "border-box",
  },
  label: {
    display:       "block",
    color:         BRAND.cream,
    fontSize:      "0.9rem",
    marginBottom:  "0.4rem",
    marginTop:     "1rem",
  },
  input: {
    width:         "100%",
    padding:       "0.75rem",
    borderRadius:  "8px",
    border:        "1px solid rgba(74,164,225,0.5)",
    background:    "rgba(255,255,255,0.1)",
    color:         "white",
    fontSize:      "1rem",
    boxSizing:     "border-box",
  },
};

